// Trekkin.app — PanelNav.jsx v7.4.7 (FINAL + HUD GPS MODE A)
// -------------------------------------------------------------
// 🔥 Inclui HUDCard (dados GPSHybrid globais) abaixo dos 4 botões
// 🔥 Nenhuma lógica alterada (permissões, gravação, trilhos, LIVE)
// 🔥 Totalmente compatível com PanelShell + Regra de Ouro
// -------------------------------------------------------------

import { useRef } from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { usePanelsStore } from "../stores/usePanelsStore.jsx";
import MiniMapRealTimeStatic from "./MiniMapRealTimeStatic.jsx";
import TrekkinChart from "./TrekkinChart.jsx";
import CloseButton from "./CloseButton.jsx";
import TrekkinButton from "./TrekkinButton.jsx";
import TrekkinConfirm from "./TrekkinConfirm.jsx";
import StatsPanelIcons from "./StatsPanelIcons.jsx";
import { parseGPX } from "../functions/parseGPX.js";
import { useShareStore } from "../stores/useShareStore.jsx";
import { showToast } from "./NotifyToast.jsx";
import TrailItem from "./TrailItem.jsx";
import { useTranslate } from "../i18n/useTranslate";

import { useCan } from "../hooks/useCan.js";
import { useAuthStore } from "../stores/useAuthStore";
import { useGroupStore } from "../stores/useGroupStore.jsx";
import { useEventsStore } from "../stores/useEventsStore.jsx";
import { useActivityModeStore } from "../stores/useActivityModeStore.jsx";
import { db } from "../firebaseConfig";
import { get as getDb, onValue, ref } from "firebase/database";
import { getTrailNameKeys } from "../utils/trailNameSync.js";
import {
  readPendingTrailDeletes,
  removePendingTrailDelete,
} from "../utils/trailPendingDelete.js";
import {
  buildTrailGpx,
  writeTrailGpxNative,
  readTrailGpxNative,
  isNativePlatform as isNativeStorage,
} from "../utils/trailNative.js";
import { buildGpxFetchUrl, normalizeTrekkinUrl } from "../utils/normalizeTrekkinUrl.js";

import HUDCard from "./HUDCard.jsx";   // 🆕 HUD GPS MODE A
import ActiveWeatherForecast from "./ActiveWeatherForecast.jsx";
import { Capacitor } from "@capacitor/core";
import {
  calculateTrackElevationStats,
  sanitizeElevationPoints,
} from "../utils/elevationStats.js";
import {
  calculateRecordingMetrics,
  prepareRecordingMetricPoints,
} from "../utils/recordingMetrics.js";
import { estimateActiveCalories } from "../utils/calorieEstimator.js";
import { logEvent } from "../stores/useLogsStore.jsx";
import { useHeartRateStore } from "../stores/useHeartRateStore.js";
import {
  initNativeHeartRate,
  startNativeHeartRate,
  pauseNativeHeartRate,
  resumeNativeHeartRate,
  syncNativeHeartRateSamples,
  stopNativeHeartRate,
} from "../utils/nativeHeartRate.js";
import {
  ACTIVITY_MODE_BIKE,
  ACTIVITY_MODE_WALK,
  isActivityModeCompatible,
} from "../utils/activityMode.js";

const IOS_RECORDING_GPS_PROFILE_KEY = "trekkin.ios.recordingGpsProfile";
const IOS_RECORDING_GPS_PROFILE_KEYS = ["precision", "balanced", "battery"];
const PUBLIC_TRAILS_MAP_CACHE_KEY = "trekkin_public_trails_map_cache_v1";

// -------------------------------------------------------------
// Utils
// -------------------------------------------------------------
const haversine = (a, b) => {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const bearingDeg = (from, to) => {
  if (!from || !to) return null;
  const lat1 = (Number(from.lat) * Math.PI) / 180;
  const lat2 = (Number(to.lat) * Math.PI) / 180;
  const dLon = ((Number(to.lon) - Number(from.lon)) * Math.PI) / 180;
  if (!Number.isFinite(lat1) || !Number.isFinite(lat2) || !Number.isFinite(dLon)) {
    return null;
  }
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  return (deg + 360) % 360;
};

const headingDiffDeg = (a, b) => {
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return 180;
  const d = Math.abs(((x - y + 540) % 360) - 180);
  return Number.isFinite(d) ? d : 180;
};

const calcTotalDistance = (pts) => {
  if (!Array.isArray(pts) || pts.length < 2) return 0;
  let d = 0;
  for (let i = 1; i < pts.length; i++) d += haversine(pts[i - 1], pts[i]);
  return d / 1000;
};

const calcPositiveAscent = (pts) => {
  return calculateTrackElevationStats(pts).gain;
};

const parseMetricNumber = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  const raw = String(value).trim();
  if (!raw) return NaN;
  const n = Number(raw.replace(",", ".").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(n) ? n : NaN;
};

const formatHMS = (s) => {
  const sec = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const ss = sec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(ss)}`;
};

const formatRemainingDistance = (meters) => {
  const m = Number(meters || 0);
  if (!Number.isFinite(m) || m <= 0) return "0 m";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(2)} km`;
};

const formatRemainingTime = (seconds) => {
  const sec = Number(seconds || 0);
  if (!Number.isFinite(sec) || sec <= 0) return "—";
  const min = Math.ceil(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const formatHistoryDefaultName = (ts = Date.now()) => {
  const d = new Date(Number(ts) || Date.now());
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = String(d.getFullYear()).slice(-2);
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dd}/${mm}/${yy} - ${hh}:${min}`;
};

const normalizeLegacyTrailAutoName = (raw) =>
  String(raw || "")
    .replace(/\.gpx$/i, "")
    .replace(/^srv-/i, "")
    .replace(/^trekkin[-_\s]*/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/(?:\s|[-_()])\d{8,}\s*$/g, "")
    .replace(/\d{10,}\s*$/g, "")
    .trim()
    .toLowerCase();

const prettifyTrailNameText = (raw, fallback = "Trilho") => {
  const pickFileLikeName = (value) => {
    const text = String(value || "").trim();
    if (!text) return "";
    let fromUrl = text;
    try {
      const u = new URL(text);
      fromUrl = u.pathname.split("/").pop() || text;
    } catch {
      const noQuery = text.split("?")[0];
      fromUrl = noQuery.split("/").pop() || noQuery;
    }
    try {
      return decodeURIComponent(fromUrl);
    } catch {
      return fromUrl;
    }
  };
  const source = pickFileLikeName(raw) || String(raw || fallback);
  const base = source
    .replace(/\.gpx$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/(?:\s|[-_()])\d{8,}\s*$/g, "")
    .replace(/\d{10,}\s*$/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!base) return fallback;
  if (base.toLowerCase() !== base) return base;
  return base.replace(/\b\w/g, (c) => c.toUpperCase());
};

const shouldCleanupLegacyTrailName = (trail, currentName) => {
  const current = String(currentName || "").trim();
  if (!current) return false;
  if (/^trekkin[-_\s]/i.test(current)) return true;
  if (/^srv-/i.test(current)) return true;
  if (/(?:\s|[-_()])\d{8,}\s*$/.test(current)) return true;
  if (/\d{10,}\s*$/.test(current)) return true;
  if (/\.gpx$/i.test(current)) return true;

  const normalizedCurrent = normalizeLegacyTrailAutoName(current);
  const cloudFile = String(trail?.cloud?.file || "").trim();
  if (cloudFile) {
    const normalizedFile = normalizeLegacyTrailAutoName(cloudFile);
    if (normalizedCurrent && normalizedCurrent === normalizedFile) return true;
  }
  const cloudUrl = String(trail?.cloud?.url || "").trim();
  if (cloudUrl) {
    let fileFromUrl = cloudUrl;
    try {
      const u = new URL(cloudUrl);
      fileFromUrl = u.pathname.split("/").pop() || cloudUrl;
    } catch {
      fileFromUrl = cloudUrl.split("?")[0].split("/").pop() || cloudUrl;
    }
    const normalizedUrlFile = normalizeLegacyTrailAutoName(fileFromUrl);
    if (normalizedCurrent && normalizedCurrent === normalizedUrlFile) return true;
  }
  const id = String(trail?.id || "").trim();
  if (id) {
    const normalizedId = normalizeLegacyTrailAutoName(id);
    if (normalizedCurrent && normalizedCurrent === normalizedId) return true;
  }
  return false;
};

const OFF_TRAIL_THRESHOLD_VALUES = [20, 40, 60, 80, 100];

const readBoolLocal = (key, fallback = true) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === undefined) return fallback;
    return raw === "1";
  } catch {
    return fallback;
  }
};

const readOffTrailThreshold = (fallback = 40) => {
  try {
    const raw = Number(localStorage.getItem("offTrailThresholdM"));
    if (!Number.isFinite(raw)) return fallback;
    if (OFF_TRAIL_THRESHOLD_VALUES.includes(raw)) return raw;
  } catch {}
  return fallback;
};

// -------------------------------------------------------------
// Component Principal
// -------------------------------------------------------------
export default function PanelNav(props = {}) {
  const isRecording = usePanelsStore((s) => s.isRecording);
  const isRecordingPaused = usePanelsStore((s) => s.isRecordingPaused);
  const recordingMode = usePanelsStore((s) => s.recordingMode || "normal");
  const setRecordingMode = usePanelsStore((s) => s.setRecordingMode);
  const activityMode = useActivityModeStore((s) => s.activityMode);
  const setActivityMode = useActivityModeStore((s) => s.setActivityMode);
  const planActive = usePanelsStore((s) => s.planActive);
  const selectedEvent = usePanelsStore((s) => s.selectedEvent);
  const user = useAuthStore((s) => s.user);
  const currentGroup = useGroupStore((s) => s.currentGroup);
  const events = useEventsStore((s) => s.events);
  const isEventLiveFn = useEventsStore((s) => s.isEventLive);
  const getEventRouteForMap = useEventsStore((s) => s.getEventRouteForMap);
  const can = useCan();
  const canUseHeartRate = can("PanelNav", "heart_rate_sensors");
  const translate = useTranslate();
  const [showBgPermission, setShowBgPermission] = useState(false);
  const [androidBatteryInfo, setAndroidBatteryInfo] = useState(null);
  const [recordingGpsProfile, setRecordingGpsProfile] = useState(() => {
    try {
      const saved = String(
        localStorage.getItem(IOS_RECORDING_GPS_PROFILE_KEY) || ""
      ).trim();
      return IOS_RECORDING_GPS_PROFILE_KEYS.includes(saved) ? saved : "balanced";
    } catch {
      return "balanced";
    }
  });
  const bgPermissionPromptedRef = useRef(false);
  const pendingStartAfterBgRef = useRef(false);
  const points = usePanelsStore((s) => s.points);
  const userPosition = usePanelsStore((s) => s.userPosition);
  const trailBase = usePanelsStore((s) => s.trailBase);
  const trailData = usePanelsStore((s) => s.trailData);
  const recordingStartTime = usePanelsStore((s) => s.recordingStartTime);
  const recordingStopTime = usePanelsStore((s) => s.recordingStopTime);
  const recordingPausedAt = usePanelsStore((s) => s.recordingPausedAt);
  const recordingPausedDurationMs = usePanelsStore((s) => s.recordingPausedDurationMs);
  const heartRateBpm = useHeartRateStore((s) => s.bpm);
  const heartRateSamples = useHeartRateStore((s) => s.samples);
  const heartRateStatus = useHeartRateStore((s) => s.status);
  const heartRateLastSampleAt = useHeartRateStore((s) => s.lastSampleAt);
  const heartRateDeviceName = useHeartRateStore((s) => s.deviceName);
  const heartRateSource = useHeartRateStore((s) => s.source);
  const androidHeartRateReconnecting =
    Capacitor.getPlatform() === "android" &&
    ["scanning", "connecting", "disconnected"].includes(heartRateStatus);

  const {
    setPoints,
    setTrailBase,
    setFollowProfilePoints,
    setTrailData,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    setActivePanel,
    navWaypointTarget,
    setNavWaypointAddMode,
    navWaypointPending,
    navWaypointQueue = [],
    clearNavWaypoint,
    startPlanRoute,
  } = usePanelsStore();
  const isExpeditionMode = recordingMode === "expedition";

  const handleRecordingGpsProfileChange = (profile) => {
    const next = IOS_RECORDING_GPS_PROFILE_KEYS.includes(profile)
      ? profile
      : "balanced";
    setRecordingGpsProfile(next);
    try {
      localStorage.setItem(IOS_RECORDING_GPS_PROFILE_KEY, next);
    } catch {}
    showToast("Preferência GPS guardada. Será aplicada na próxima gravação.", "success");
  };

  useEffect(() => {
    initNativeHeartRate().catch(() => {});
  }, []);

  useEffect(() => {
    if (Capacitor.getPlatform() !== "android") return undefined;
    const reconcileAfterResume = () => {
      if (document.visibilityState !== "visible") return;
      const state = usePanelsStore.getState();
      if (
        !state.isRecording ||
        state.isRecordingPaused ||
        heartRateRecoveryBlockedRef.current
      ) return;
      syncNativeHeartRateSamples({ reason: "document_visible" }).catch(() => {});
    };
    document.addEventListener("visibilitychange", reconcileAfterResume);
    window.addEventListener("focus", reconcileAfterResume);
    return () => {
      document.removeEventListener("visibilitychange", reconcileAfterResume);
      window.removeEventListener("focus", reconcileAfterResume);
    };
  }, []);

  const heartRateRecoveryStartedRef = useRef(false);
  const heartRateRecoveryBlockedRef = useRef(false);
  const heartRateRecoveryTimerRef = useRef(null);
  const heartRateLastRecoveryAtRef = useRef(0);
  useEffect(() => {
    if (heartRateRecoveryTimerRef.current) {
      window.clearTimeout(heartRateRecoveryTimerRef.current);
      heartRateRecoveryTimerRef.current = null;
    }
    if (
      !isRecording ||
      isRecordingPaused ||
      heartRateRecoveryBlockedRef.current ||
      heartRateRecoveryStartedRef.current
    ) return;
    if (document.visibilityState !== "visible") return;
    const staleAfterMs = 20000;
    const lastSampleAgeMs = heartRateLastSampleAt
      ? Math.max(0, Date.now() - Number(heartRateLastSampleAt))
      : Number.POSITIVE_INFINITY;
    if ([
      "switching",
      "requestingPermission",
      "authorizationRequested",
      "paused",
    ].includes(heartRateStatus)) return;

    // A sessão persistida pode restaurar uma última amostra e marcar o estado
    // web como ativo, mesmo que o processo nativo Bluetooth tenha reiniciado.
    // Nesse caso esperamos apenas pela janela normal de amostragem e voltamos
    // a ligar o sensor se não chegar nenhuma leitura nova.
    const isConnectionPending = [
      "connecting",
      "connected",
      "scanning",
      "waitingForSensor",
    ].includes(heartRateStatus);
    const recoveryDelayMs =
      heartRateStatus === "active" && lastSampleAgeMs < staleAfterMs
        ? Math.max(2500, staleAfterMs - lastSampleAgeMs + 500)
        : isConnectionPending
          ? 15000
          : 2500;

    heartRateRecoveryTimerRef.current = window.setTimeout(() => {
      const now = Date.now();
      if (now - heartRateLastRecoveryAtRef.current < 10000) return;
      const state = usePanelsStore.getState();
      if (!state.isRecording || state.isRecordingPaused || document.visibilityState !== "visible") return;
      const currentHeartRate = useHeartRateStore.getState();
      const currentSampleAgeMs = currentHeartRate.lastSampleAt
        ? Math.max(0, now - Number(currentHeartRate.lastSampleAt))
        : Number.POSITIVE_INFINITY;
      if (currentHeartRate.status === "active" && currentSampleAgeMs < staleAfterMs) return;
      heartRateLastRecoveryAtRef.current = now;
      heartRateRecoveryStartedRef.current = true;
      logEvent("warn", "HEART_RATE_STALE_SESSION_RECOVERY", {
        platform: Capacitor.getPlatform?.() || "web",
        status: currentHeartRate.status,
        lastSampleAgeMs: Number.isFinite(currentSampleAgeMs)
          ? Math.round(currentSampleAgeMs)
          : null,
        deviceName: currentHeartRate.deviceName || null,
        source: currentHeartRate.source || null,
      });
      resumeNativeHeartRate({
        activity: activityMode === ACTIVITY_MODE_BIKE ? "cycling" : "walking",
      })
        .catch((error) => {
          logEvent("warn", "HEART_RATE_RECOVERY_FAILED", {
            error: error?.message || String(error),
          });
        })
        .finally(() => {
          heartRateRecoveryStartedRef.current = false;
        });
    }, recoveryDelayMs);

    return () => {
      if (heartRateRecoveryTimerRef.current) {
        window.clearTimeout(heartRateRecoveryTimerRef.current);
        heartRateRecoveryTimerRef.current = null;
      }
    };
  }, [
    activityMode,
    heartRateLastSampleAt,
    heartRateStatus,
    isRecording,
    isRecordingPaused,
  ]);

  const { isSharing, isStartingShare, shareId } = useShareStore();

  const [liveLink, setLiveLink] = useState(() => {
    try { return localStorage.getItem("liveShareLink") || ""; }
    catch { return ""; }
  });

  useEffect(() => {
    if (isSharing && shareId) {
      const link = `https://trekkin.pt/app/?share=${shareId}`;
      setLiveLink(link);
      try { localStorage.setItem("liveShareLink", link); } catch {}
      return;
    }
    setLiveLink("");
    try { localStorage.removeItem("liveShareLink"); } catch {}
  }, [isSharing, shareId]);

  useEffect(() => {
    if (!isRecording || !isExpeditionMode || !isSharing) return;
    let cancelled = false;
    (async () => {
      try {
        await useShareStore.getState().stopShare?.();
        if (!cancelled) {
          setLiveLink("");
          try { localStorage.removeItem("liveShareLink"); } catch {}
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [isRecording, isExpeditionMode, isSharing]);

  const [trilhos, setTrilhos] = useState([]);
  const visibleTrilhos = useMemo(
    () =>
      (Array.isArray(trilhos) ? trilhos : []).filter((trail) =>
        isActivityModeCompatible(trail?.activityMode, activityMode)
      ),
    [trilhos, activityMode]
  );
  const [showTrailsOnMap, setShowTrailsOnMap] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.__trekkinNavTrailsMapOn === true;
  });

  const trailsMapDispatchSeqRef = useRef(0);
  const [activeTrail, setActiveTrail] = useState(null);
  const [drawTrailPoints, setDrawTrailPoints] = useState([]);
  const [drawTrailActive, setDrawTrailActive] = useState(false);
  const [drawTrailPreviewOpen, setDrawTrailPreviewOpen] = useState(false);
  const isTrailCreateMode = props?.mode === "trailCreate";
  const lastNonEmptyDrawTrailPointsRef = useRef([]);
  const [clockNow, setClockNow] = useState(Date.now());
  const trailNamesRef = useRef({});
  const [isStopping, setIsStopping] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showNavAltimetry, setShowNavAltimetry] = useState(true);
  const [showNavHeartRate, setShowNavHeartRate] = useState(true);
  const stopInFlightRef = useRef(false);
  const autoPauseTriggeredRef = useRef(false);
  const eventTrailCacheRef = useRef({ key: null, points: [] });
  const followSessionRef = useRef({
    key: null,
    startTs: 0,
    startMeters: 0,
    lastMeters: 0,
  });
  const followMatchRef = useRef({
    routeKey: null,
    idx: -1,
    progressMeters: 0,
    ts: 0,
  });
  const followMotionRef = useRef({
    lat: null,
    lon: null,
    ts: 0,
    heading: null,
  });
  const hadFollowRouteRef = useRef(false);
  const [eventTrailBase, setEventTrailBase] = useState([]);
  const [navRoutePreview, setNavRoutePreview] = useState([]);
  const [isActiveEventLiveResolved, setIsActiveEventLiveResolved] = useState(false);
  const [followDirectionReversed, setFollowDirectionReversed] = useState(() =>
    readBoolLocal("trekkin_follow_direction_reversed", false)
  );
  const [offTrailThresholdM, setOffTrailThresholdM] = useState(() =>
    readOffTrailThreshold(40)
  );

  const isAndroidNative =
    Capacitor?.isNativePlatform?.() && Capacitor.getPlatform?.() === "android";

  const refreshAndroidBatteryInfo = useCallback(async () => {
    if (!isAndroidNative) return null;
    const plugin =
      window?.Capacitor?.Plugins?.GPSNativePlugin ||
      window?.Capacitor?.Plugins?.GPSNative;
    if (!plugin) return null;
    try {
      const info =
        (await plugin.getBatteryOptimizationInfo?.()) ||
        (await plugin.isIgnoringBatteryOptimizations?.());
      if (!info) return null;
      const normalized = {
        ignoring: !!info.ignoring,
        powerSaveMode: !!info.powerSaveMode,
        manufacturer: String(info.manufacturer || info.brand || "").trim(),
        model: String(info.model || "").trim(),
      };
      setAndroidBatteryInfo(normalized);
      return normalized;
    } catch {
      return null;
    }
  }, [isAndroidNative]);

  useEffect(() => {
    if (!isAndroidNative) return;
    refreshAndroidBatteryInfo();
  }, [isAndroidNative, refreshAndroidBatteryInfo]);

  useEffect(() => {
    if (!isAndroidNative || !isRecording) return;
    refreshAndroidBatteryInfo();
    const id = setInterval(() => {
      refreshAndroidBatteryInfo();
    }, 15000);
    return () => clearInterval(id);
  }, [isAndroidNative, isRecording, refreshAndroidBatteryInfo]);

  const hasManualTrailBase = Array.isArray(trailBase) && trailBase.length > 1;
  const hasAnyNavActive =
    hasManualTrailBase || !!navWaypointTarget || !!navWaypointPending;
  const activeTrailIdForUi = useMemo(() => {
    const id = activeTrail?.id ?? trailData?.id ?? null;
    if (id == null) return "";
    return String(id);
  }, [activeTrail?.id, trailData?.id]);

  // Rehidrata o trilho ativo quando o painel volta a abrir:
  // o estado local perde-se no unmount, mas o trailData permanece no store.
  useEffect(() => {
    if (!hasManualTrailBase) return;
    if (activeTrail?.id != null) return;
    if (trailData?.id == null) return;
    setActiveTrail({
      id: trailData.id,
      nome: trailData?.nome || trailData?.name || null,
      name: trailData?.name || trailData?.nome || null,
    });
  }, [
    hasManualTrailBase,
    activeTrail?.id,
    trailData?.id,
    trailData?.nome,
    trailData?.name,
  ]);

  const activeEventId = useMemo(() => {
    if (!currentGroup || typeof currentGroup !== "object") return null;
    return (
      currentGroup.activeEventId ||
      currentGroup.activeEvent ||
      currentGroup.eventId ||
      currentGroup.guideOnlineEventId ||
      null
    );
  }, [currentGroup]);

  const eventsArr = useMemo(() => {
    if (Array.isArray(events)) return events;
    if (events && typeof events === "object") return Object.values(events);
    return [];
  }, [events]);

  const activeEventFromGroup = useMemo(() => {
    if (!activeEventId) return null;
    const wanted = String(activeEventId);
    const fromEvents =
      eventsArr.find(
        (ev) => String(ev?.id || ev?.eventId || "") === wanted
      ) || null;
    if (fromEvents) return fromEvents;
    if (
      currentGroup?.event &&
      String(currentGroup?.event?.id || currentGroup?.event?.eventId || "") === wanted
    ) {
      return currentGroup.event;
    }
    if (
      selectedEvent &&
      String(selectedEvent?.id || selectedEvent?.eventId || "") === wanted
    ) {
      return selectedEvent;
    }
    return null;
  }, [activeEventId, eventsArr, currentGroup?.event, selectedEvent]);

  const liveRouteUrl = useMemo(() => {
    const liveByEvent =
      activeEventFromGroup?.isLive === true ||
      activeEventFromGroup?.live === true ||
      activeEventFromGroup?.status === "live";
    const liveByStore =
      activeEventId && typeof isEventLiveFn === "function"
        ? (() => {
            try {
              return !!isEventLiveFn(activeEventId);
            } catch {
              return false;
            }
          })()
        : false;
    const canUseLiveRoute = !!activeEventId && (liveByEvent || liveByStore);
    const routeCandidate =
      canUseLiveRoute && typeof getEventRouteForMap === "function"
        ? getEventRouteForMap(activeEventId)
        : null;

    return normalizeTrekkinUrl(
      (canUseLiveRoute
        ? activeEventFromGroup?.routeGPXUrl ||
          activeEventFromGroup?.routeGpxUrl ||
          activeEventFromGroup?.routeUrl ||
          activeEventFromGroup?.routeFileUrl ||
          activeEventFromGroup?.gpxUrl ||
          activeEventFromGroup?.gpx ||
          null
        : null) ||
        currentGroup?.routeGPXUrl ||
        currentGroup?.routeGpxUrl ||
        currentGroup?.routeUrl ||
        currentGroup?.routeFileUrl ||
        currentGroup?.gpxUrl ||
        currentGroup?.gpx ||
        routeCandidate?.routeGPXUrl ||
        routeCandidate?.event?.routeGPXUrl ||
        routeCandidate?.event?.routeGpxUrl ||
        routeCandidate?.event?.routeUrl ||
        routeCandidate?.event?.routeFileUrl ||
        routeCandidate?.event?.gpxUrl ||
        routeCandidate?.event?.gpx ||
        null
    );
  }, [
    activeEventId,
    activeEventFromGroup,
    currentGroup,
    getEventRouteForMap,
    isEventLiveFn,
  ]);

  const isActiveEventLive = useMemo(() => {
    if (!activeEventId || typeof isEventLiveFn !== "function") return false;
    try {
      return !!isEventLiveFn(activeEventId);
    } catch {
      return false;
    }
  }, [activeEventId, isEventLiveFn, events]);

  useEffect(() => {
    const reloadAlertPrefs = () => {
      setOffTrailThresholdM(readOffTrailThreshold(40));
    };
    reloadAlertPrefs();
    window.addEventListener("trekkin:alerts-auto-changed", reloadAlertPrefs);
    return () => {
      window.removeEventListener(
        "trekkin:alerts-auto-changed",
        reloadAlertPrefs
      );
    };
  }, []);

  const base = import.meta.env.BASE_URL || "/";
  const canUploadTrail =
    user?.role === "admin" ||
    can("PanelNav", "upload_gpx") ||
    can("PanelHistory", "upload_gpx");
  const canSyncTrails =
    !!user?.uid &&
    (canUploadTrail ||
      can("PanelNav", "start_trail") ||
      can("PanelTrail", "view_trail") ||
      can("PanelTrailCreate", "create_trail"));

  const parseTrailsList = (raw, fallback = []) => {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  };

  const applyTrailNames = (list, namesMap) => {
    if (!Array.isArray(list) || !list.length) {
      return { list: Array.isArray(list) ? list : [], changed: false };
    }
    let changed = false;
    const next = list.map((trail) => {
      const keys = getTrailNameKeys(trail);
      const remoteName = keys
        .map((key) => namesMap?.[key])
        .find((name) => !!name);
      const currentName = trail?.nome || trail?.name || "";
      let nextName = remoteName || currentName;

      if (!remoteName && shouldCleanupLegacyTrailName(trail, currentName)) {
        const source =
          trail?.cloud?.file ||
          trail?.cloud?.url ||
          currentName ||
          trail?.id ||
          "";
        nextName = prettifyTrailNameText(
          source,
          currentName || translate("trail.fallback.trail")
        );
      }

      if (!nextName || currentName === nextName) return trail;
      changed = true;
      return { ...trail, nome: nextName, name: nextName };
    });
    return { list: next, changed };
  };

  const saveTrilhos = (data) => {
    setTrilhos(data);
    try { localStorage.setItem("trilhos", JSON.stringify(data)); } catch {}
  };
  const markTrailPendingUpload = (id) => {
    if (!id) return;
    try {
      const raw = localStorage.getItem("trailsPendingUpload") || "{}";
      const state = JSON.parse(raw);
      if (typeof state === "object" && state) {
        state[id] = Date.now();
        localStorage.setItem("trailsPendingUpload", JSON.stringify(state));
      }
    } catch {}
  };
  const clearTrailPendingUpload = (id) => {
    if (!id) return;
    try {
      const raw = localStorage.getItem("trailsPendingUpload") || "{}";
      const state = JSON.parse(raw);
      if (!state || typeof state !== "object") return;
      if (state[id]) {
        delete state[id];
        localStorage.setItem("trailsPendingUpload", JSON.stringify(state));
      }
    } catch {}
  };

  // -------------------------------------------------------------
  // Guardar trilhos
  // -------------------------------------------------------------
  useEffect(() => {
    const reload = () => {
      try {
        const raw = localStorage.getItem("trilhos");
        const list = parseTrailsList(raw, []);
        const { list: next, changed } = applyTrailNames(
          list,
          trailNamesRef.current
        );
        if (changed) saveTrilhos(next);
        else setTrilhos(next);
      } catch {}
    };
    reload();
    window.addEventListener("trekkin:trails-updated", reload);
    return () => window.removeEventListener("trekkin:trails-updated", reload);
  }, [drawTrailPoints]);

  const buildStartFromPoints = (trail) => {
    const toCoord = (value) => {
      if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
      if (typeof value === "string") {
        const cleaned = value.trim().replace(",", ".");
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) ? parsed : NaN;
      }
      return NaN;
    };
    const pts = Array.isArray(trail?.points) ? trail.points : [];
    const first = pts.length ? pts[0] : null;
    const latFromFirst = Array.isArray(first)
      ? toCoord(first[0])
      : toCoord(first?.lat ?? first?.latitude);
    const lonFromFirst = Array.isArray(first)
      ? toCoord(first[1])
      : toCoord(first?.lon ?? first?.lng ?? first?.longitude);
    return {
      latFromFirst,
      lonFromFirst,
    };
  };

  const pickStart = (trail) => {
      const { latFromFirst, lonFromFirst } = buildStartFromPoints(trail);
      const latCandidates = [
        trail?.startLat,
        trail?.start?.lat,
        trail?.cloud?.startLat,
        trail?.cloud?.meta?.startLat,
        latFromFirst,
      ];
      const lonCandidates = [
        trail?.startLon,
        trail?.start?.lon,
        trail?.cloud?.startLon,
        trail?.cloud?.meta?.startLon,
        lonFromFirst,
      ];
      const toCoord = (value) => {
        if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
        if (typeof value === "string") {
          const cleaned = value.trim().replace(",", ".");
          const parsed = Number(cleaned);
          return Number.isFinite(parsed) ? parsed : NaN;
        }
        return NaN;
      };
      const lat = latCandidates.map((v) => toCoord(v)).find((v) => Number.isFinite(v));
      const lon = lonCandidates.map((v) => toCoord(v)).find((v) => Number.isFinite(v));
      return {
        lat: Number.isFinite(lat) ? lat : null,
        lon: Number.isFinite(lon) ? lon : null,
      };
    };

  const resolveNavTrailMapUrl = (it) => {
    const uid = user?.uid || null;
    const raw =
      it?.cloud?.url ||
      it?.url ||
      it?.cloud?.path ||
      it?.cloud?.file ||
      null;
    if (!raw) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    const isBareFile = !trimmed.includes("/") && /\.gpx$/i.test(trimmed);
    if (isBareFile) {
      if (uid) {
        return buildGpxFetchUrl(`/app/app-upload/uploads/usersgpx/trails/${uid}/${trimmed}`);
      }
      return buildGpxFetchUrl(`/app/app-upload/uploads/usersgpx/trails/${trimmed}`);
    }
    const normalized = normalizeTrekkinUrl(trimmed);
    return buildGpxFetchUrl(normalized);
  };

  const dispatchTrailsMapPayloads = async (on) => {
    if (typeof window === "undefined") return;
    const seq = ++trailsMapDispatchSeqRef.current;
    window.__trekkinNavTrailsMapOn = !!on;
    const isCurrentSeq = () => seq === trailsMapDispatchSeqRef.current;
    const fetchWithTimeout = async (url, ms = 7000) => {
      const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
      const timer = ctrl
        ? setTimeout(() => {
            try {
              ctrl.abort();
            } catch {}
          }, ms)
        : null;
      try {
        const res = await fetch(url, ctrl ? { signal: ctrl.signal } : undefined);
        return res;
      } finally {
        if (timer) clearTimeout(timer);
      }
    };

    const withStartSnapshot = (it, start) => ({
      id: it?.id || null,
      name: it?.nome || it?.name || it?.id || "Trilho",
      shortDescription: String(it?.shortDescription || ""),
      galleryImages: Array.isArray(it?.galleryImages) ? it.galleryImages : [],
      file: typeof it?.file === "string" ? it.file : null,
      cloud: it?.cloud || null,
      gpx: typeof it?.gpx === "string" ? it.gpx : null,
      points: Array.isArray(it?.points) ? it.points : [],
      startLat: start.lat,
      startLon: start.lon,
    });

    const resolveFirstPointFromAny = async (it) => {
      const parseAndGetFirst = async (content) => {
        if (!content || typeof content !== "string") return null;
        const parsed = await parseGPX(content);
        const pts = Array.isArray(parsed?.points) ? parsed.points : [];
        if (!pts.length) return null;
        const first = pts[0];
        if (!first) return null;
        const lat = Number(first?.lat ?? first?.latitude);
        const lon = Number(first?.lon ?? first?.lng ?? first?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
        return { lat, lon, points: pts };
      };

      try {
        if (typeof it?.gpx === "string" && it.gpx.trim()) {
          const hit = await parseAndGetFirst(it.gpx);
          if (hit) return hit;
        }
      } catch {}
      try {
        if (typeof it?.cloud?.gpx === "string" && it.cloud.gpx.trim()) {
          const hit = await parseAndGetFirst(it.cloud.gpx);
          if (hit) return hit;
        }
      } catch {}
      try {
        if (typeof it?.file === "string" && it.file.trim()) {
          const hit = await parseAndGetFirst(it.file);
          if (hit) return hit;
        }
      } catch {}
      try {
        const rawUrl = resolveNavTrailMapUrl(it);
        if (rawUrl) {
          const res = await fetchWithTimeout(rawUrl, 7000);
          if (res.ok) {
            const txt = await res.text();
            const hit = await parseAndGetFirst(txt);
            if (hit) return hit;
          }
        }
      } catch {}
      return null;
    };

    const navItems = (Array.isArray(visibleTrilhos) ? visibleTrilhos : []).map((it) => {
      const start = pickStart(it);
      return withStartSnapshot(it, start);
    });
    window.dispatchEvent(
      new CustomEvent("trekkin:nav-trails-map", { detail: { on: !!on, items: navItems } })
    );

    if (!on) {
      window.__trekkinPublicTrailsMapState = { on: false, items: [] };
      window.dispatchEvent(
        new CustomEvent("trekkin:public-trails-map", { detail: { on: false, items: [] } })
      );
      return;
    }

    // Ativa já a camada pública antes de sair deste painel. O pedido do
    // catálogo é assíncrono e o Painel Nav é desmontado quando regressamos ao
    // mapa; depender apenas da resposta de rede fazia desaparecer os pins dos
    // trilhos públicos em alguns WebViews. Uma cache válida permite ainda
    // mostrá-los imediatamente em ligações lentas ou temporariamente offline.
    let cachedPublicItems = [];
    try {
      const cached = JSON.parse(
        window.localStorage.getItem(PUBLIC_TRAILS_MAP_CACHE_KEY) || "[]"
      );
      cachedPublicItems = Array.isArray(cached) ? cached : [];
    } catch {}
    window.__trekkinPublicTrailsMapState = {
      on: true,
      items: cachedPublicItems,
    };
    window.dispatchEvent(
      new CustomEvent("trekkin:public-trails-map", {
        detail: { on: true, items: cachedPublicItems },
      })
    );

    // Enriquecimento assíncrono de startLat/startLon para trilhos sem início.
    try {
      const enrich = async (arr) => {
        const tasks = (Array.isArray(arr) ? arr : []).map(async (it) => {
          const hasLat = it?.startLat !== null && it?.startLat !== undefined && it?.startLat !== "";
          const hasLon = it?.startLon !== null && it?.startLon !== undefined && it?.startLon !== "";
          const lat = hasLat ? Number(it.startLat) : NaN;
          const lon = hasLon ? Number(it.startLon) : NaN;
          if (Number.isFinite(lat) && Number.isFinite(lon)) return it;
          const hit = await resolveFirstPointFromAny(it);
          if (!hit) return it;
          return {
            ...it,
            startLat: hit.lat,
            startLon: hit.lon,
            points: Array.isArray(it?.points) && it.points.length ? it.points : hit.points,
          };
        });
        const settled = await Promise.allSettled(tasks);
        return settled.map((r, idx) => (r.status === "fulfilled" ? r.value : arr[idx]));
      };

      const navEnriched = await enrich(navItems);
      if (isCurrentSeq()) {
        window.dispatchEvent(
          new CustomEvent("trekkin:nav-trails-map", { detail: { on: true, items: navEnriched } })
        );
      }
    } catch {}

    try {
      const res = await fetch("https://www.trekkin.pt/app/app-upload/list-public-gpx.php");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const publicItems = (Array.isArray(json?.items) ? json.items : []).map((it) => ({
        filename: it?.filename || null,
        name: it?.name || it?.filename || "Trilho disponível",
        url: it?.url || null,
        mtime: it?.mtime || null,
        size: it?.size || null,
        shortDescription: String(it?.shortDescription || ""),
        gallery: Array.isArray(it?.gallery) ? it.gallery : [],
        startLat:
          it?.startLat === null || it?.startLat === undefined || it?.startLat === ""
            ? null
            : Number(it.startLat),
        startLon:
          it?.startLon === null || it?.startLon === undefined || it?.startLon === ""
            ? null
            : Number(it.startLon),
      }));
      // O índice público mais antigo não contém sempre as coordenadas iniciais.
      // Sem este enriquecimento o botão ficava no estado "Limpar trilhos", mas
      // o Leaflet não recebia coordenadas válidas e não conseguia criar pins.
      const publicEnriched = await enrich(publicItems);
      if (seq === trailsMapDispatchSeqRef.current) {
        try {
          window.localStorage.setItem(
            PUBLIC_TRAILS_MAP_CACHE_KEY,
            JSON.stringify(publicEnriched)
          );
        } catch {}
        window.__trekkinPublicTrailsMapState = { on: true, items: publicEnriched };
        window.dispatchEvent(
          new CustomEvent("trekkin:public-trails-map", { detail: { on: true, items: publicEnriched } })
        );
      }
    } catch {
      if (seq === trailsMapDispatchSeqRef.current) {
        window.__trekkinPublicTrailsMapState = { on: true, items: cachedPublicItems };
        window.dispatchEvent(
          new CustomEvent("trekkin:public-trails-map", {
            detail: { on: true, items: cachedPublicItems },
          })
        );
      }
    }
  };

  useEffect(() => {
    dispatchTrailsMapPayloads(!!showTrailsOnMap);
  }, [showTrailsOnMap, visibleTrilhos, user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const namesRef = ref(db, `userTrailNames/${user.uid}`);
    const unsubscribe = onValue(namesRef, (snap) => {
      const data = snap.val() || {};
      const map = {};
      Object.entries(data).forEach(([key, value]) => {
        const name = typeof value === "string" ? value : value?.name;
        if (name) map[key] = name;
      });
      trailNamesRef.current = map;
      const local = parseTrailsList(localStorage.getItem("trilhos"), []);
      const { list, changed } = applyTrailNames(local, map);
      if (changed) {
        saveTrilhos(list);
      }
    });
    return () => unsubscribe();
  }, [user?.uid]);

  useEffect(() => {
    if (!canSyncTrails) return;
    let aborted = false;
    const syncKey = "trailsCloudSyncAt";
    const pendingKey = "trailsPendingUpload";
    const parseJson = (raw, fallback) => {
      try {
        const parsed = JSON.parse(raw);
        return parsed ?? fallback;
      } catch {
        return fallback;
      }
    };
    const markPendingAttempt = (id) => {
      const state = parseJson(localStorage.getItem(pendingKey), {});
      state[id] = Date.now();
      localStorage.setItem(pendingKey, JSON.stringify(state));
    };
    const canRetryPending = (id, minMs) => {
      const state = parseJson(localStorage.getItem(pendingKey), {});
      const last = Number(state?.[id] || 0);
      return Date.now() - last > minMs;
    };
    const syncCloud = async (force = false) => {
      if (!user?.uid) return;
      if (!force) {
        const last = Number(localStorage.getItem(syncKey) || 0);
        if (Date.now() - last < 5 * 60 * 1000) return;
      }
      try {
        const pendingDeletes = readPendingTrailDeletes();
        const pendingFiles = Object.keys(pendingDeletes || {});
        const getFilenameFromUrl = (url) => {
          if (!url) return null;
          try {
            const parsed = new URL(url, "https://www.trekkin.pt");
            return parsed.pathname.split("/").pop() || null;
          } catch {
            return String(url).split("?")[0].split("/").pop() || null;
          }
        };
        if (pendingFiles.length) {
          for (const filename of pendingFiles.slice(0, 3)) {
            try {
              const res = await fetch(
                "https://www.trekkin.pt/app/app-upload/delete-user-gpx.php",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                  },
                  body: new URLSearchParams({
                    uid: user.uid,
                    filename,
                    type: "trails",
                  }),
                }
              );
              const data = await res.json();
              if (data?.ok || data?.error === "file not found") {
                removePendingTrailDelete(filename);
              }
            } catch {}
          }
        }
        const res = await fetch(
          `https://www.trekkin.pt/app/app-upload/list-user-gpx.php?uid=${encodeURIComponent(
            user.uid
          )}&type=trails`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!data?.ok || !Array.isArray(data.items)) return;
        if (aborted) return;
        const filteredItems = pendingFiles.length
          ? data.items.filter((item) => {
              const file = item?.filename || getFilenameFromUrl(item?.url);
              return !pendingFiles.includes(file);
            })
          : data.items;
        const local = JSON.parse(localStorage.getItem("trilhos") || "[]");
        const localList = Array.isArray(local) ? local : [];
        const byCloud = new Map();
        localList.forEach((trail) => {
          if (trail?.cloud?.file) byCloud.set(trail.cloud.file, trail);
          else if (trail?.cloud?.url) byCloud.set(trail.cloud.url, trail);
        });
        const serverKeys = new Set();
        filteredItems.forEach((item) => {
          if (item?.filename) serverKeys.add(item.filename);
          if (item?.url) serverKeys.add(item.url);
        });
        let changed = false;
        const merged = localList.filter((trail) => {
          const hasCloud = trail?.cloud?.file || trail?.cloud?.url;
          if (!hasCloud) return true;
          const fileKey = trail?.cloud?.file;
          const urlKey = trail?.cloud?.url;
          const keep =
            (fileKey && serverKeys.has(fileKey)) ||
            (urlKey && serverKeys.has(urlKey));
          if (!keep) changed = true;
          return keep;
        });
        filteredItems.forEach((item) => {
          const file = item?.filename;
          const url = item?.url;
          if (!file || !url) return;
          const existing = byCloud.get(file) || byCloud.get(url);
          if (existing) {
            if (!existing.cloud?.url || existing.cloud.url !== url) {
              existing.cloud = {
                ...(existing.cloud || {}),
                file,
                url,
                syncedAt: Date.now(),
              };
              changed = true;
            }
            return;
          }
          const nameBase = prettifyTrailName(file);
          merged.push({
            id: `cloud-${item.id || file}`,
            nome: nameBase || translate("trail.fallback.trail"),
            activityMode: ACTIVITY_MODE_WALK,
            dist: "—",
            elev: "—",
            points: null,
            date: item.mtime
              ? new Date(item.mtime * 1000).toLocaleString()
              : new Date().toLocaleString(),
            cloud: { file, url, syncedAt: Date.now() },
          });
          changed = true;
        });
        const dedupeCloud = (list) => {
          const seen = new Map();
          const output = [];
          for (const item of list) {
            const cloudKey = item?.cloud?.file || item?.cloud?.url;
            if (!cloudKey) {
              output.push(item);
              continue;
            }
            const existing = seen.get(cloudKey);
            if (!existing) {
              seen.set(cloudKey, item);
              output.push(item);
              continue;
            }
            const existingPoints = Array.isArray(existing?.points)
              ? existing.points.length
              : 0;
            const itemPoints = Array.isArray(item?.points) ? item.points.length : 0;
            if (itemPoints > existingPoints) {
              const idx = output.indexOf(existing);
              if (idx >= 0) output[idx] = item;
              seen.set(cloudKey, item);
            }
            changed = true;
          }
          return output;
        };
        const deduped = dedupeCloud(merged);
        const named = applyTrailNames(deduped, trailNamesRef.current);
        if (named.changed) changed = true;
        if (changed) {
          localStorage.setItem("trilhos", JSON.stringify(named.list));
          window.dispatchEvent(new CustomEvent("trekkin:trails-updated"));
        }
        localStorage.setItem(syncKey, String(Date.now()));
      } catch {}
    };
    const syncPendingUploads = async () => {
      if (!canUploadTrail || !user?.uid) return;
      const isNative = isNativeStorage();
      if (!isNative && !navigator.onLine) return;
      const local = parseJson(localStorage.getItem("trilhos"), []);
      const localList = Array.isArray(local) ? local : [];
      const pending = localList.filter((t) => {
        if (!t || t.cloud?.file || t.cloud?.url) return false;
        const hasPoints = Array.isArray(t.points) && t.points.length > 1;
        const hasGpx = typeof t.file === "string" || typeof t.gpx === "string";
        const hasNative = !!t?.native?.trail?.path;
        return hasPoints || hasGpx || hasNative;
      });
      if (!pending.length) return;
      for (const trail of pending.slice(0, 3)) {
        if (!trail?.id) continue;
        if (!canRetryPending(trail.id, 2 * 60 * 1000)) continue;
        markPendingAttempt(trail.id);
        const safeName = buildFileSafeName(
          trail.nome || trail.name || "trilho"
        );
        let gpxText =
          typeof trail.file === "string"
            ? trail.file
            : typeof trail.gpx === "string"
            ? trail.gpx
            : "";
        if (!gpxText && isNative) {
          try {
            const nativeGpx = await readTrailGpxNative(trail);
            if (nativeGpx) gpxText = nativeGpx;
          } catch {}
        }
        if (!gpxText) {
          const uploadPoints = Array.isArray(trail?.points) ? trail.points : [];
          if (!uploadPoints.length) continue;
          gpxText = buildTrailGpx(
            uploadPoints,
            trail.nome || "Trekkin Track"
          );
        }
        const gpxBlob = new Blob([gpxText], { type: "application/gpx+xml" });
        const form = new FormData();
        form.append("uid", user.uid);
        form.append("type", "trails");
        form.append("file", gpxBlob, `${safeName}_${Date.now()}.gpx`);
        try {
          const res = await fetch(
            "https://www.trekkin.pt/app/app-upload/upload-user-gpx.php",
            { method: "POST", body: form }
          );
          const data = await res.json();
          if (!data?.ok || !data?.filename || !data?.url) continue;
          const raw = localStorage.getItem("trilhos") || "[]";
          const list = parseJson(raw, []);
          const next = Array.isArray(list)
            ? list.map((t) =>
                t.id === trail.id
                  ? {
                      ...t,
                      cloud: {
                        file: data.filename,
                        url: data.url,
                        uploadedAt: Date.now(),
                        autoSynced: true,
                      },
                    }
                  : t
              )
            : list;
          localStorage.setItem("trilhos", JSON.stringify(next));
          window.dispatchEvent(new CustomEvent("trekkin:trails-updated"));
          const state = parseJson(localStorage.getItem(pendingKey), {});
          delete state[trail.id];
          localStorage.setItem(pendingKey, JSON.stringify(state));
        } catch {}
      }
    };
    syncCloud();
    syncPendingUploads();
    window.addEventListener("online", syncCloud);
    window.addEventListener("online", syncPendingUploads);
    window.addEventListener("trekkin:trails-updated", syncPendingUploads);
    const interval = window.setInterval(syncPendingUploads, 60 * 1000);
    return () => {
      aborted = true;
      window.removeEventListener("online", syncCloud);
      window.removeEventListener("online", syncPendingUploads);
      window.removeEventListener("trekkin:trails-updated", syncPendingUploads);
      window.clearInterval(interval);
    };
  }, [canSyncTrails, user?.uid]);

  useEffect(() => {
    const normalizeRoutePath = (path) =>
      (Array.isArray(path) ? path : [])
        .map((p) => ({
          lat: Number(p?.lat),
          lon: Number(p?.lon ?? p?.lng),
          ele: Number.isFinite(Number(p?.ele)) ? Number(p.ele) : undefined,
        }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));

    const onNavRoute = (ev) => {
      const path = Array.isArray(ev?.detail?.path) ? ev.detail.path : [];
      const normalized = normalizeRoutePath(path);
      setNavRoutePreview(normalized.length > 1 ? normalized : []);
    };
    if (typeof window !== "undefined") {
      const bootPath = Array.isArray(window.__trekkinLastNavGridRoute?.path)
        ? window.__trekkinLastNavGridRoute.path
        : [];
      const bootNormalized = normalizeRoutePath(bootPath);
      if (bootNormalized.length > 1) {
        setNavRoutePreview(bootNormalized);
      }
      window.addEventListener("trekkin:navgrid-route", onNavRoute);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("trekkin:navgrid-route", onNavRoute);
      }
    };
  }, []);

  useEffect(() => {
    if (!navWaypointTarget && !navWaypointPending) {
      setNavRoutePreview([]);
    }
  }, [navWaypointTarget, navWaypointPending]);

  useEffect(() => {
    let cancelled = false;
    let running = false;
    let rerunRequested = false;

    const resolveTrailCloudUrl = (trail) => {
      const raw =
        trail?.cloud?.url ||
        trail?.cloud?.path ||
        trail?.cloud?.file ||
        trail?.url ||
        null;
      if (!raw) return null;
      const text = String(raw).trim();
      if (!text) return null;
      const isBareFile = !text.includes("/") && /\.gpx$/i.test(text);
      if (isBareFile && user?.uid) {
        return buildGpxFetchUrl(
          `/app/app-upload/uploads/usersgpx/trails/${encodeURIComponent(user.uid)}/${text}`
        );
      }
      const normalized = normalizeTrekkinUrl(text);
      return buildGpxFetchUrl(normalized);
    };

    const enrichTrailMetricsIfMissing = async (trail) => {
      if (!trail || typeof trail !== "object") return trail;
      const distNow =
        parseMetricNumber(trail?.dist) ||
        parseMetricNumber(trail?.distanceKm) ||
        parseMetricNumber(trail?.distKm) ||
        parseMetricNumber(trail?.stats?.distKm);
      const elevNow =
        parseMetricNumber(trail?.elev) ||
        parseMetricNumber(trail?.dPlus) ||
        parseMetricNumber(trail?.stats?.dPlus) ||
        parseMetricNumber(trail?.stats?.elev);
      const needsDist = !(Number.isFinite(distNow) && distNow > 0);
      const needsElev = !(Number.isFinite(elevNow) && elevNow > 0);
      if (!needsDist && !needsElev) return trail;

      let points = Array.isArray(trail?.points) ? trail.points : [];
      if (points.length < 2 && typeof trail?.file === "string" && trail.file.trim()) {
        try {
          const parsed = await parseGPX(trail.file);
          points = Array.isArray(parsed?.points) ? parsed.points : [];
        } catch {}
      }
      if (points.length < 2 && typeof trail?.gpx === "string" && trail.gpx.trim()) {
        try {
          const parsed = await parseGPX(trail.gpx);
          points = Array.isArray(parsed?.points) ? parsed.points : [];
        } catch {}
      }
      if (points.length < 2) {
        try {
          const url = resolveTrailCloudUrl(trail);
          if (url) {
            const res = await fetch(url);
            if (res.ok) {
              const txt = await res.text();
              const parsed = await parseGPX(txt);
              points = Array.isArray(parsed?.points) ? parsed.points : [];
            }
          }
        } catch {}
      }
      if (points.length < 2) return trail;

      const distKm = calcTotalDistance(points);
      const dPlus = calcPositiveAscent(points);
      const nextDist =
        needsDist && distKm > 0 ? `${distKm.toFixed(1)} km` : trail?.dist;
      const nextElev =
        needsElev && dPlus > 0 ? `${Math.round(dPlus)} m D+` : trail?.elev;

      if (
        String(nextDist || "").trim() === String(trail?.dist || "").trim() &&
        String(nextElev || "").trim() === String(trail?.elev || "").trim()
      ) {
        return trail;
      }

      return {
        ...trail,
        dist: nextDist ?? trail?.dist,
        elev: nextElev ?? trail?.elev,
      };
    };

    const run = async () => {
      if (running) {
        rerunRequested = true;
        return;
      }
      running = true;
      try {
        const local = parseTrailsList(localStorage.getItem("trilhos"), []);
        if (!Array.isArray(local) || !local.length) return;
        let changed = false;
        const next = [...local];
        for (let i = 0; i < next.length; i += 1) {
          const enriched = await enrichTrailMetricsIfMissing(next[i]);
          if (enriched !== next[i]) {
            next[i] = enriched;
            changed = true;
          }
        }
        if (!changed || cancelled) return;
        const named = applyTrailNames(next, trailNamesRef.current);
        if (cancelled) return;
        saveTrilhos(named.list);
      } finally {
        running = false;
        if (rerunRequested && !cancelled) {
          rerunRequested = false;
          run();
        }
      }
    };

    run();
    const onUpdate = () => run();
    window.addEventListener("trekkin:trails-updated", onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("trekkin:trails-updated", onUpdate);
    };
  }, [user?.uid]);

  useEffect(() => {
    const applyPayload = (payload) => {
      if (!payload || payload.context !== "trail") return;
      setDrawTrailActive(!!payload.active);
      const pts = Array.isArray(payload.points) ? payload.points : [];
      if (pts.length > 1) {
        lastNonEmptyDrawTrailPointsRef.current = pts;
        setDrawTrailPoints(pts);
      } else if (!isTrailCreateMode) {
        setDrawTrailPoints(pts);
      }
      if (!pts.length) setDrawTrailPreviewOpen(false);
    };
    const handler = (event) => {
      const payload = event?.detail || window?.__trekkinTrailDraw || {};
      applyPayload(payload);
    };
    // Rehidrata ao abrir o painel (ex.: voltar do mapa para criação de trilhos).
    applyPayload(window?.__trekkinTrailDraw || {});
    window.addEventListener("trekkin:trail-draw-updated", handler);
    return () =>
      window.removeEventListener("trekkin:trail-draw-updated", handler);
  }, [isTrailCreateMode]);

  const drawTrailPreviewPoints = useMemo(
    () =>
      (Array.isArray(drawTrailPoints) ? drawTrailPoints : [])
        .map((p) => ({
          lat: Number(p?.lat),
          lon: Number(p?.lon ?? p?.lng),
          ele: Number.isFinite(Number(p?.ele)) ? Number(p.ele) : undefined,
        }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon)),
    [drawTrailPoints]
  );

  const effectiveDrawTrailPoints = useMemo(() => {
    if (drawTrailPreviewPoints.length > 1) return drawTrailPreviewPoints;
    const fallback = window?.__trekkinTrailDraw?.points;
    const next = (Array.isArray(fallback) ? fallback : [])
      .map((p) => ({
        lat: Number(p?.lat),
        lon: Number(p?.lon ?? p?.lng),
        ele: Number.isFinite(Number(p?.ele)) ? Number(p.ele) : undefined,
      }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    return next;
  }, [drawTrailPreviewPoints]);

  useEffect(() => {
    if (isTrailCreateMode && drawTrailPreviewPoints.length > 1) {
      setDrawTrailPreviewOpen(true);
    }
  }, [isTrailCreateMode, drawTrailPreviewPoints.length]);

  useEffect(() => {
    if (!isTrailCreateMode) return;
    const payload = window?.__trekkinTrailDraw || {};
    if (payload?.context !== "trail") return;
    const pts = Array.isArray(payload.points) ? payload.points : [];
    if (pts.length > 1) {
      lastNonEmptyDrawTrailPointsRef.current = pts;
      setDrawTrailPoints(pts);
    } else if (lastNonEmptyDrawTrailPointsRef.current.length > 1) {
      setDrawTrailPoints(lastNonEmptyDrawTrailPointsRef.current);
    }
  }, [isTrailCreateMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOpenTrailCreate = (event) => {
      const raw = Array.isArray(event?.detail?.points) ? event.detail.points : [];
      const pts = raw
        .map((p) => ({
          lat: Number(p?.lat),
          lon: Number(p?.lon ?? p?.lng),
          ele: Number.isFinite(Number(p?.ele)) ? Number(p.ele) : undefined,
        }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
      if (pts.length > 1) {
        lastNonEmptyDrawTrailPointsRef.current = pts;
        setDrawTrailPoints(pts);
        setDrawTrailPreviewOpen(true);
      }
    };
    window.addEventListener("trekkin:trail-create-open", onOpenTrailCreate);
    return () =>
      window.removeEventListener("trekkin:trail-create-open", onOpenTrailCreate);
  }, []);

  const downsamplePoints = (list, maxPoints) => {
    if (!Array.isArray(list) || list.length <= maxPoints) return list;
    const step = Math.ceil(list.length / maxPoints);
    const sampled = [];
    for (let i = 0; i < list.length; i += step) {
      sampled.push(list[i]);
    }
    const last = list[list.length - 1];
    if (sampled[sampled.length - 1] !== last) sampled.push(last);
    return sampled;
  };

  const buildFileSafeName = (name, fallback = "trilho") => {
    const raw = String(name || fallback).trim().replace(/\.gpx$/i, "");
    const slug = raw
      .replace(/[^a-zA-Z0-9_-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    return slug || fallback;
  };

  const prettifyTrailName = (file) => {
    return prettifyTrailNameText(file, translate("trail.fallback.trail"));
  };

  const saveDrawnTrailWithPoints = async (rawPoints) => {
    if (!Array.isArray(rawPoints) || rawPoints.length < 2) {
      showToast(translate("nav.draw.notEnoughPoints"));
      return;
    }
    const points = rawPoints.map((p) => ({
      lat: p.lat,
      lon: p.lon,
    }));
    const trailName = translate("nav.draw.defaultName");
    const distKmVal = calcTotalDistance(points);
    const isNative = isNativeStorage();
    let nativeRef = null;
    if (isNative) {
      try {
        const gpxText = buildTrailGpx(rawPoints, trailName);
        if (gpxText) {
          nativeRef = await writeTrailGpxNative(gpxText, trailName);
        }
      } catch {}
    }
    const baseTrail = {
      id: `trail-${Date.now()}`,
      nome: trailName,
      activityMode,
      dist: distKmVal ? `${distKmVal.toFixed(1)} km` : "—",
      elev: "—",
      date: new Date().toLocaleString(),
      pointsTotal: points.length,
      native: nativeRef ? { trail: nativeRef } : undefined,
    };
    const buildEntry = (trailPoints) => ({ ...baseTrail, points: trailPoints });
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem("trilhos") || "[]");
      if (!Array.isArray(existing)) existing = [];
    } catch {
      existing = [];
    }
    const trySave = (trailPoints) => {
      const updated = [buildEntry(trailPoints), ...existing];
      try {
        localStorage.setItem("trilhos", JSON.stringify(updated));
        return true;
      } catch (err) {
        console.warn("⚠️ Falha ao guardar trilho desenhado:", err);
        return false;
      }
    };
    let savedOk = trySave(points);
    let reducedNotice = false;
    if (!savedOk) {
      savedOk = trySave(downsamplePoints(points, 20000));
      reducedNotice = savedOk;
    }
    if (!savedOk) {
      savedOk = trySave(downsamplePoints(points, 12000));
      reducedNotice = savedOk;
    }
    if (!savedOk) {
      savedOk = trySave(downsamplePoints(points, 6000));
      reducedNotice = savedOk;
    }
    if (!savedOk) {
      savedOk = trySave(downsamplePoints(points, 2000));
      reducedNotice = savedOk;
    }
    if (!savedOk) {
      showToast(translate("nav.draw.saveFailed"), "error");
      return;
    }
    if (reducedNotice) {
      showToast("Trilho guardado com redução de pontos por limite de armazenamento.", "info");
    }
    markTrailPendingUpload(baseTrail.id);
    window.dispatchEvent(new CustomEvent("trekkin:trails-updated"));
    if (canUploadTrail && user?.uid) {
      const uploadPoints = Array.isArray(points) ? points : [];
      const gpxText = buildTrailGpx(
        uploadPoints,
        baseTrail.nome || "Trekkin Track"
      );
      const gpxBlob = new Blob([gpxText], { type: "application/gpx+xml" });
      const form = new FormData();
      form.append("uid", user.uid);
      form.append("type", "trails");
      const safeName = buildFileSafeName(baseTrail.nome || "trilho");
      form.append("file", gpxBlob, `${safeName}_${Date.now()}.gpx`);
      fetch("https://www.trekkin.pt/app/app-upload/upload-user-gpx.php", {
        method: "POST",
        body: form,
      })
        .then((res) => res.json())
        .then((data) => {
          if (!data?.ok || !data?.filename || !data?.url) return;
          const file = data.filename;
          const cloudUrl = data.url;
          try {
            const raw = localStorage.getItem("trilhos") || "[]";
            const list = JSON.parse(raw);
            const next = Array.isArray(list)
              ? list.map((t) =>
                  t.id === baseTrail.id
                    ? {
                        ...t,
                        cloud: { file, url: cloudUrl, uploadedAt: Date.now() },
                      }
                    : t
                )
              : list;
            localStorage.setItem("trilhos", JSON.stringify(next));
            window.dispatchEvent(new CustomEvent("trekkin:trails-updated"));
            clearTrailPendingUpload(baseTrail.id);
            const plannedRaw =
              localStorage.getItem("plannedTrailFiles") || "[]";
            const plannedList = Array.isArray(JSON.parse(plannedRaw))
              ? JSON.parse(plannedRaw)
              : [];
            if (!plannedList.includes(file)) {
              plannedList.push(file);
              localStorage.setItem(
                "plannedTrailFiles",
                JSON.stringify(plannedList)
              );
            }
          } catch {}
        })
        .catch((err) => {
          console.warn("⚠️ Upload GPX falhou:", err);
        });
    }
    window.dispatchEvent(new CustomEvent("trekkin:trail-draw-clear"));
    window.dispatchEvent(new CustomEvent("trekkin:trail-draw-stop"));
    setDrawTrailPoints([]);
    setDrawTrailActive(false);
    showToast(translate("nav.draw.saved"), "success");
  };

  const saveDrawnTrail = async () => saveDrawnTrailWithPoints(drawTrailPoints);
  const saveCurrentDrawTrail = async () =>
    saveDrawnTrailWithPoints(effectiveDrawTrailPoints);

  useEffect(() => {
    const handler = (event) => {
      const pts = Array.isArray(event?.detail?.points) ? event.detail.points : [];
      if (pts.length < 2) return;
      setDrawTrailPoints(pts);
      saveDrawnTrailWithPoints(pts);
    };
    window.addEventListener("trekkin:trail-draw-save", handler);
    return () => window.removeEventListener("trekkin:trail-draw-save", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload = window.__trekkinTrailDrawSave;
    const pts = Array.isArray(payload?.points) ? payload.points : [];
    if (pts.length < 2) return;
    window.__trekkinTrailDrawSave = null;
    setDrawTrailPoints(pts);
    saveDrawnTrailWithPoints(pts);
  }, [saveDrawnTrailWithPoints]);

  // -------------------------------------------------------------
  // Métricas da gravação
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isRecording) {
      setClockNow(Date.now());
      return;
    }
    const id = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isRecording]);

  useEffect(() => {
    if (!isRecording) return;
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform?.() !== "android") return;
    let cancelled = false;
    const refreshCompleteRecording = async () => {
      try {
        await window?.__TREKKIN_ANDROID_FLUSH_RECORDING_POINTS?.("panel_nav_open");
        if (!cancelled) setClockNow(Date.now());
      } catch (error) {
        logEvent("warn", "PANEL_NAV_METRICS_REFRESH_FAIL", {
          error: error?.message || String(error),
        });
      }
    };
    refreshCompleteRecording();
    return () => {
      cancelled = true;
    };
  }, [isRecording]);

  const metricPoints = useMemo(
    () => prepareRecordingMetricPoints(points, {
      android: Capacitor.isNativePlatform() && Capacitor.getPlatform?.() === "android",
    }),
    [points]
  );

  const { totalSec, movingSec, stoppedSec, distKm, dPlus, avgMovingKmh } =
    useMemo(() => {
      if (!isRecording && !recordingStartTime) {
        return { totalSec: 0, movingSec: 0, stoppedSec: 0, distKm: 0, dPlus: 0, avgMovingKmh: 0 };
      }
      const totalPausedMs =
        Number(recordingPausedDurationMs || 0) +
        (isRecordingPaused && recordingPausedAt
          ? Math.max(0, clockNow - Number(recordingPausedAt))
          : 0);
      const metrics = calculateRecordingMetrics(metricPoints, {
        startedAt: recordingStartTime,
        stoppedAt: isRecording ? clockNow : recordingStopTime,
        pausedMilliseconds: totalPausedMs,
      });
      return {
        totalSec: metrics.totalSeconds,
        movingSec: metrics.movingSeconds,
        stoppedSec: metrics.stoppedSeconds,
        distKm: metrics.distanceKm,
        dPlus: metrics.elevationGain,
        avgMovingKmh: metrics.averageMovingKmh,
      };
    }, [
      metricPoints,
      recordingStartTime,
      recordingStopTime,
      isRecordingPaused,
      recordingPausedAt,
      recordingPausedDurationMs,
      isRecording,
      clockNow,
    ]);

  useEffect(() => {
    let cancelled = false;

    const clearEventTrailBase = (keepLive = false) => {
      eventTrailCacheRef.current = { key: null, points: [] };
      setIsActiveEventLiveResolved(!!keepLive);
      setEventTrailBase((prev) => (prev.length ? [] : prev));
    };

    const resolveLiveContext = async () => {
      if (!activeEventId) return { isLive: false, routeUrl: null };
      let liveData = null;
      try {
        const liveSnap = await getDb(ref(db, `eventsLive/${activeEventId}`));
        liveData = liveSnap?.val?.() || null;
      } catch {}

      const routeFromLiveNode = normalizeTrekkinUrl(
        liveData?.routeGPXUrl ||
          liveData?.routeGpxUrl ||
          liveData?.routeUrl ||
          liveData?.routeFileUrl ||
          liveData?.gpxUrl ||
          liveData?.gpx ||
          null
      );

      const liveByNode = !!(liveData && liveData?.isLive === true);
      const isLive = !!(isActiveEventLive || liveByNode);
      if (!isLive) return { isLive: false, routeUrl: null };

      return {
        isLive: true,
        routeUrl: normalizeTrekkinUrl(routeFromLiveNode || liveRouteUrl || null),
      };
    };

    const loadEventTrailBase = async () => {
      if (hasManualTrailBase) {
        clearEventTrailBase(false);
        return;
      }

      if (!activeEventId) {
        clearEventTrailBase(false);
        return;
      }

      const resolved = await resolveLiveContext();
      if (cancelled) return;
      const isLive = !!resolved?.isLive;
      const routeUrl = resolved?.routeUrl || null;
      if (!isLive) {
        clearEventTrailBase(false);
        return;
      }
      if (!routeUrl) {
        clearEventTrailBase(true);
        return;
      }
      setIsActiveEventLiveResolved(true);

      const routeKey = `${activeEventId}|${routeUrl}`;
      if (
        eventTrailCacheRef.current.key === routeKey &&
        Array.isArray(eventTrailCacheRef.current.points) &&
        eventTrailCacheRef.current.points.length > 1
      ) {
        setEventTrailBase(eventTrailCacheRef.current.points);
        return;
      }

      try {
        const fetchUrl = buildGpxFetchUrl(routeUrl);
        if (!fetchUrl) {
          setEventTrailBase((prev) => (prev.length ? [] : prev));
          return;
        }
        const res = await fetch(fetchUrl, { cache: "no-store" });
        if (!res.ok) throw new Error(`route_fetch_${res.status}`);
        const gpxText = await res.text();
        const parsed = await parseGPX(gpxText);
        const basePoints = Array.isArray(parsed?.points) ? parsed.points : [];
        if (cancelled) return;
        eventTrailCacheRef.current = { key: routeKey, points: basePoints };
        setEventTrailBase(basePoints);
      } catch {
        if (cancelled) return;
        clearEventTrailBase(true);
      }
    };

    loadEventTrailBase();
    return () => {
      cancelled = true;
    };
  }, [activeEventId, liveRouteUrl, trailBase, isActiveEventLive]);

  // -------------------------------------------------------------
  // Fora do trilho
  // -------------------------------------------------------------
  const lastPoint = points.length > 0 ? points[points.length - 1] : null;
  const instSpeed =
    (lastPoint?.spd ?? userPosition?.spd ?? 0) < 0
      ? 0
      : lastPoint?.spd ?? userPosition?.spd ?? 0;

  const lat = userPosition?.lat ?? lastPoint?.lat;
  const lon = userPosition?.lon ?? lastPoint?.lon;
  const baseFollowChartPoints = useMemo(() => {
    if (Array.isArray(navRoutePreview) && navRoutePreview.length > 1) return navRoutePreview;
    if (Array.isArray(trailBase) && trailBase.length > 1) return trailBase;
    if (
      isActiveEventLiveResolved &&
      activeEventId &&
      Array.isArray(eventTrailBase) &&
      eventTrailBase.length > 1
    )
      return eventTrailBase;
    return [];
  }, [
    navRoutePreview,
    trailBase,
    eventTrailBase,
    activeEventId,
    isActiveEventLiveResolved,
  ]);

  const followSourceRouteKey = useMemo(() => {
    if (!Array.isArray(baseFollowChartPoints) || baseFollowChartPoints.length < 2) {
      return "";
    }
    const first = baseFollowChartPoints[0] || {};
    const last = baseFollowChartPoints[baseFollowChartPoints.length - 1] || {};
    return [
      activeEventId || trailData?.id || activeTrail?.id || "local",
      baseFollowChartPoints.length,
      Number(first.lat || 0).toFixed(5),
      Number(first.lon ?? first.lng ?? 0).toFixed(5),
      Number(last.lat || 0).toFixed(5),
      Number(last.lon ?? last.lng ?? 0).toFixed(5),
    ].join("|");
  }, [baseFollowChartPoints, activeEventId, trailData?.id, activeTrail?.id]);

  useEffect(() => {
    followMatchRef.current = {
      routeKey: null,
      idx: -1,
      progressMeters: 0,
      ts: 0,
    };
  }, [followSourceRouteKey]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "trekkin_follow_direction_reversed",
        followDirectionReversed ? "1" : "0"
      );
    } catch {}
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("trekkin:follow-direction-changed", {
            detail: { reversed: !!followDirectionReversed },
          })
        );
      }
    } catch {}
  }, [followDirectionReversed]);

  const followChartPoints = useMemo(() => {
    if (!Array.isArray(baseFollowChartPoints) || baseFollowChartPoints.length < 2) {
      return [];
    }
    if (!followDirectionReversed) return baseFollowChartPoints;
    return [...baseFollowChartPoints].reverse();
  }, [baseFollowChartPoints, followDirectionReversed]);

  useEffect(() => {
    setFollowProfilePoints(followChartPoints);
  }, [followChartPoints, setFollowProfilePoints]);

  const showFollowChart = followChartPoints.length > 1;
  const showRecordingChart = isRecording || points.length > 1;
  const followChartHeight = showRecordingChart ? 96 : 120;
  const recordingChartHeight = showFollowChart ? 96 : 120;
  const followPosition = userPosition || lastPoint || null;

  useEffect(() => {
    const hasFollowRoute =
      Array.isArray(baseFollowChartPoints) && baseFollowChartPoints.length > 1;
    const hadFollowRoute = hadFollowRouteRef.current;

    // Só volta ao sentido original quando o seguimento termina mesmo.
    if (hadFollowRoute && !hasFollowRoute && followDirectionReversed) {
      setFollowDirectionReversed(false);
    }

    hadFollowRouteRef.current = hasFollowRoute;
  }, [baseFollowChartPoints, followDirectionReversed]);

  useEffect(() => {
    const posLat = Number(followPosition?.lat);
    const posLon = Number(followPosition?.lon ?? followPosition?.lng);
    if (!Number.isFinite(posLat) || !Number.isFinite(posLon)) return;

    const now = Date.now();
    const prev = followMotionRef.current || {};
    let heading = Number(prev?.heading);
    if (!Number.isFinite(heading)) heading = null;

    if (Number.isFinite(prev?.lat) && Number.isFinite(prev?.lon)) {
      const moved = haversine(
        { lat: Number(prev.lat), lon: Number(prev.lon) },
        { lat: posLat, lon: posLon }
      );
      const dtSec = Math.max(0.001, (now - Number(prev?.ts || now)) / 1000);
      const speedMps = moved / dtSec;
      if (moved >= 3 && speedMps >= 0.6) {
        const inferred = bearingDeg(
          { lat: Number(prev.lat), lon: Number(prev.lon) },
          { lat: posLat, lon: posLon }
        );
        if (Number.isFinite(inferred)) heading = inferred;
      }
    }

    followMotionRef.current = {
      lat: posLat,
      lon: posLon,
      ts: now,
      heading,
    };
  }, [followPosition?.lat, followPosition?.lon, followPosition?.lng]);

  const followGeometry = useMemo(() => {
    if (!showFollowChart) return null;
    if (!Array.isArray(followChartPoints) || followChartPoints.length < 2)
      return null;

    const first = followChartPoints[0] || {};
    const last = followChartPoints[followChartPoints.length - 1] || {};
    const routeKey = [
      activeEventId || trailData?.id || activeTrail?.id || "local",
      followChartPoints.length,
      Number(first.lat || 0).toFixed(5),
      Number(first.lon ?? first.lng ?? 0).toFixed(5),
      Number(last.lat || 0).toFixed(5),
      Number(last.lon ?? last.lng ?? 0).toFixed(5),
      followDirectionReversed ? "rev" : "fwd",
    ].join("|");

    const cumulative = new Array(followChartPoints.length).fill(0);
    let total = 0;
    for (let i = 1; i < followChartPoints.length; i++) {
      total += haversine(followChartPoints[i - 1], followChartPoints[i]);
      cumulative[i] = total;
    }

    const posLat = Number(followPosition?.lat);
    const posLon = Number(followPosition?.lon ?? followPosition?.lng);
    const explicitHeading = Number(
      followPosition?.heading ??
      followPosition?.course ??
      followPosition?.cog ??
      followPosition?.brg
    );
    const motionHeading = Number(followMotionRef.current?.heading);
    const userHeading = Number.isFinite(explicitHeading)
      ? explicitHeading
      : Number.isFinite(motionHeading)
      ? motionHeading
      : null;
    let progress = 0;
    let matchedIndex = -1;
    let nearestMeters = Number.POSITIVE_INFINITY;
    if (Number.isFinite(posLat) && Number.isFinite(posLon)) {
      const pointDistances = new Array(followChartPoints.length).fill(
        Number.POSITIVE_INFINITY
      );
      for (let i = 0; i < followChartPoints.length; i++) {
        const p = followChartPoints[i];
        const lat = Number(p?.lat);
        const lon = Number(p?.lon ?? p?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
        const dMeters = haversine(
          { lat: posLat, lon: posLon },
          { lat, lon }
        );
        pointDistances[i] = dMeters;
        if (dMeters < nearestMeters) {
          nearestMeters = dMeters;
        }
      }

      const rankedByDistance = [];
      for (let i = 0; i < pointDistances.length; i++) {
        const d = Number(pointDistances[i]);
        if (!Number.isFinite(d)) continue;
        rankedByDistance.push({ idx: i, d });
      }
      rankedByDistance.sort((a, b) => a.d - b.d);

      const candidateIdxSet = new Set();
      for (let i = 0; i < Math.min(14, rankedByDistance.length); i++) {
        candidateIdxSet.add(rankedByDistance[i].idx);
      }

      const nowTs = Date.now();
      const prevMatch = followMatchRef.current || {};
      const sameRoute =
        prevMatch?.routeKey === routeKey &&
        Number.isFinite(prevMatch?.idx) &&
        prevMatch.idx >= 0 &&
        nowTs - Number(prevMatch?.ts || 0) <= 8 * 60 * 1000;
      const prevIdx = sameRoute ? Number(prevMatch.idx) : -1;
      const prevProgress = sameRoute
        ? Number(prevMatch.progressMeters || 0)
        : 0;

      if (sameRoute && prevIdx >= 0) {
        const from = Math.max(0, prevIdx - 40);
        const to = Math.min(followChartPoints.length - 1, prevIdx + 80);
        for (let i = from; i <= to; i++) candidateIdxSet.add(i);
      }

      if (!candidateIdxSet.size && rankedByDistance.length) {
        candidateIdxSet.add(rankedByDistance[0].idx);
      }

      const candidateIndices = Array.from(candidateIdxSet).sort((a, b) => {
        const da = Number(pointDistances[a]);
        const db = Number(pointDistances[b]);
        if (Number.isFinite(da) && Number.isFinite(db)) return da - db;
        if (Number.isFinite(da)) return -1;
        if (Number.isFinite(db)) return 1;
        return a - b;
      });

      let bestScore = Number.POSITIVE_INFINITY;
      let bestIdx = -1;
      for (const idx of candidateIndices) {
        const dMeters = Number(pointDistances[idx]);
        if (!Number.isFinite(dMeters)) continue;

        const candidateProgress = Number(cumulative[idx] || 0);
        let continuityPenalty = 0;
        if (sameRoute && prevIdx >= 0) {
          const idxDelta = idx - prevIdx;
          const progressDelta = candidateProgress - prevProgress;
          continuityPenalty += Math.abs(idxDelta) * 1.2;
          continuityPenalty += Math.abs(progressDelta) * 0.015;
          if (progressDelta < -25) {
            continuityPenalty +=
              120 + Math.min(220, Math.abs(progressDelta) * 0.1);
          }
          if (progressDelta > 2500) {
            continuityPenalty +=
              90 + Math.min(180, (progressDelta - 2500) * 0.05);
          }
          if (idxDelta < -6) {
            continuityPenalty += 80 + Math.min(160, Math.abs(idxDelta) * 3);
          }
        }

        let headingPenalty = 0;
        if (Number.isFinite(userHeading)) {
          let segBearing = null;
          if (idx < followChartPoints.length - 1) {
            segBearing = bearingDeg(followChartPoints[idx], followChartPoints[idx + 1]);
          }
          if (!Number.isFinite(segBearing) && idx > 0) {
            segBearing = bearingDeg(followChartPoints[idx - 1], followChartPoints[idx]);
          }
          if (Number.isFinite(segBearing)) {
            const diff = headingDiffDeg(userHeading, segBearing);
            headingPenalty = diff * 1.35;
            if (diff > 120) headingPenalty += 80;
          }
        }

        const score = dMeters + continuityPenalty + headingPenalty;
        if (score < bestScore) {
          bestScore = score;
          bestIdx = idx;
        }
      }

      if (bestIdx >= 0) {
        matchedIndex = bestIdx;
        progress = Number(cumulative[bestIdx] || 0);
      }
    }

    return {
      routeKey,
      totalMeters: total,
      progressMeters: progress,
      remainingMeters: Math.max(0, total - progress),
      nearestMeters,
      matchedIndex,
    };
  }, [
    showFollowChart,
    followChartPoints,
    followPosition?.lat,
    followPosition?.lon,
    followPosition?.lng,
    followPosition?.heading,
    followPosition?.course,
    followPosition?.cog,
    followPosition?.brg,
    activeEventId,
    trailData?.id,
    activeTrail?.id,
    followDirectionReversed,
  ]);

  useEffect(() => {
    if (!followGeometry || !showFollowChart) {
      followMatchRef.current = {
        routeKey: null,
        idx: -1,
        progressMeters: 0,
        ts: 0,
      };
      return;
    }

    const idx = Number(followGeometry?.matchedIndex);
    if (!Number.isFinite(idx) || idx < 0) return;
    followMatchRef.current = {
      routeKey: followGeometry.routeKey,
      idx: Math.floor(idx),
      progressMeters: Number(followGeometry.progressMeters || 0),
      ts: Date.now(),
    };
  }, [
    showFollowChart,
    followGeometry?.routeKey,
    followGeometry?.matchedIndex,
    followGeometry?.progressMeters,
  ]);

  const offTrailThresholdValue = Number(offTrailThresholdM || 40);
  const isOnTrailForProgress =
    Number.isFinite(followGeometry?.nearestMeters) &&
    Number(followGeometry?.nearestMeters) <= offTrailThresholdValue;

  useEffect(() => {
    if (!followGeometry || !isOnTrailForProgress) {
      followSessionRef.current = {
        key: null,
        startTs: 0,
        startMeters: 0,
        lastMeters: 0,
      };
      return;
    }

    const now = Date.now();
    const session = followSessionRef.current;
    const routeChanged = session.key !== followGeometry.routeKey;
    const movedBack = followGeometry.progressMeters + 25 < session.lastMeters;

    if (routeChanged || movedBack || !session.startTs) {
      followSessionRef.current = {
        key: followGeometry.routeKey,
        startTs: now,
        startMeters: followGeometry.progressMeters,
        lastMeters: followGeometry.progressMeters,
      };
      return;
    }

    followSessionRef.current = {
      ...session,
      key: followGeometry.routeKey,
      lastMeters: followGeometry.progressMeters,
    };
  }, [followGeometry?.routeKey, followGeometry?.progressMeters, isOnTrailForProgress]);

  const followEstimate = useMemo(() => {
    if (!followGeometry) return null;
    if (!isOnTrailForProgress) {
      return {
        remainingMeters: followGeometry.totalMeters,
        remainingSec: null,
      };
    }

    const now = Date.now();
    const session = followSessionRef.current || {};
    let avgMps = null;

    if (session.key === followGeometry.routeKey && session.startTs > 0) {
      const progressed = Math.max(
        0,
        followGeometry.progressMeters - Number(session.startMeters || 0)
      );
      const elapsedSec = Math.max(0, (now - session.startTs) / 1000);
      if (progressed >= 30 && elapsedSec >= 30) {
        avgMps = progressed / elapsedSec;
      }
    }

    const instMps = Number(instSpeed || 0);
    if (!Number.isFinite(avgMps) || avgMps <= 0.2) {
      if (Number.isFinite(instMps) && instMps > 0.5) avgMps = instMps;
    }

    const remainingSec =
      Number.isFinite(avgMps) && avgMps > 0.2
        ? followGeometry.remainingMeters / avgMps
        : null;

    return {
      remainingMeters: followGeometry.remainingMeters,
      remainingSec,
    };
  }, [
    followGeometry,
    instSpeed,
    followPosition?.lat,
    followPosition?.lon,
    isOnTrailForProgress,
  ]);

  const showFollowProgressMarker = isOnTrailForProgress;

  // -------------------------------------------------------------
  // Gravação
  // -------------------------------------------------------------
  const withUiTimeout = (promise, ms, label) =>
    Promise.race([
      Promise.resolve(promise),
      new Promise((_, reject) =>
        window.setTimeout(() => reject(new Error(label)), ms)
      ),
    ]);

  const handleStartRecording = async () => {
    heartRateRecoveryBlockedRef.current = false;
    heartRateRecoveryStartedRef.current = false;
    setPoints([]);

    // 🔴 ANDROID: limpar sessão anterior + dizer que AGORA está a gravar
    if (Capacitor.isNativePlatform()) {
      const plugin =
        window?.Capacitor?.Plugins?.GPSNativePlugin ||
        window?.Capacitor?.Plugins?.GPSNative;

      if (Capacitor.getPlatform?.() === "android") {
        try {
          await plugin?.clearRecordingSnapshot?.();
        } catch {}
        try {
          await plugin?.setRecording?.({ recording: true });
          await plugin?.startTracking?.();
          const status = await plugin?.getRecordingStatus?.();
          logEvent("system", "ANDROID_RECORDING_START_CONFIRMED", {
            recording: !!status?.recording,
            hasFile: !!status?.hasFile,
            sessionId: status?.sessionId || null,
          });
          if (status && status.recording === false) {
            throw new Error("Native recording did not start");
          }
        } catch (error) {
          logEvent("error", "ANDROID_RECORDING_START_FAILED", {
            error: error?.message || String(error),
          });
          showToast("Não foi possível iniciar o GPS no Android.", "error");
          return false;
        }
      } else {
        plugin?.setRecording?.({ recording: true });
      }
    }

    startRecording({ mode: isExpeditionMode ? "expedition" : "normal" });
    if (canUseHeartRate) {
      startNativeHeartRate({
        activity: activityMode === ACTIVITY_MODE_BIKE ? "cycling" : "walking",
      }).catch((error) => {
        logEvent("warn", "HEART_RATE_START_FAILED", {
          error: error?.message || String(error),
        });
      });
    } else {
      logEvent("info", "HEART_RATE_START_SKIPPED", {
        reason: "feature_not_allowed",
        platform: Capacitor.getPlatform?.() || "web",
        supported: useHeartRateStore.getState().supported,
        permission: useHeartRateStore.getState().permission,
      });
      stopNativeHeartRate().catch(() => null);
    }

    const pos = usePanelsStore.getState().userPosition;
    if (pos?.lat && pos?.lon) {
      const ts = Date.now();
      usePanelsStore.getState().addPoint({
        lat: pos.lat,
        lon: pos.lon,
        ele: pos.ele ?? 0,
        acc: pos.acc ?? null,
        spd: pos.spd ?? 0,
        ts,
        time: ts,
      });
    }
    return true;
  };

  const handlePauseRecording = (reason = "manual") => {
    logEvent("system", "RECORDING_PAUSE_TRIGGER", {
      reason,
      platform: Capacitor.getPlatform?.() || "web",
      points: Array.isArray(usePanelsStore.getState().points)
        ? usePanelsStore.getState().points.length
        : 0,
    });
    pauseRecording();
    if (canUseHeartRate) pauseNativeHeartRate().catch(() => {});
    if (Capacitor.isNativePlatform()) {
      const plugin =
        window?.Capacitor?.Plugins?.GPSNativePlugin ||
        window?.Capacitor?.Plugins?.GPSNative;
      plugin?.setRecording?.({ recording: false });
      plugin?.stopTracking?.();
      plugin?.stopForeground?.();
    }
  };

  const handleResumeRecording = async () => {
    if (Capacitor.isNativePlatform()) {
      const plugin =
        window?.Capacitor?.Plugins?.GPSNativePlugin ||
        window?.Capacitor?.Plugins?.GPSNative;
      if (Capacitor.getPlatform?.() === "android") {
        try {
          await plugin?.setRecording?.({ recording: true });
          await plugin?.startTracking?.();
          const status = await plugin?.getRecordingStatus?.();
          if (!status?.recording) {
            throw new Error("Native recording did not resume");
          }
          logEvent("system", "ANDROID_RECORDING_RESUME_CONFIRMED", {
            sessionId: status?.sessionId || null,
            hasFile: !!status?.hasFile,
          });
        } catch (error) {
          logEvent("error", "ANDROID_RECORDING_RESUME_FAILED", {
            error: error?.message || String(error),
          });
          showToast("Não foi possível retomar o GPS. Tenta novamente.", "error");
          return false;
        }
      } else {
        await plugin?.setRecording?.({ recording: true });
        await plugin?.startTracking?.();
      }
      plugin?.getCurrentPositionOnce?.();
    }
    resumeRecording();
    if (canUseHeartRate) {
      resumeNativeHeartRate({
        activity: activityMode === ACTIVITY_MODE_BIKE ? "cycling" : "walking",
      }).catch(() => {});
    }
    showToast(translate("nav.toast.resumedRecording"), "success");
    return true;
  };

  const handleResumeWithGuards = async () => {
    const state = usePanelsStore.getState();
    const startedAt = Number(state?.recordingStartTime || 0);
    const routeName = formatHistoryDefaultName(startedAt || Date.now());
    const now = new Date();
    const startDate = startedAt ? new Date(startedAt) : null;
    const isDifferentDay =
      !!startDate &&
      (now.getFullYear() !== startDate.getFullYear() ||
        now.getMonth() !== startDate.getMonth() ||
        now.getDate() !== startDate.getDate());

    if (isDifferentDay) {
      logEvent("system", "RECORDING_RESUME_CROSS_DAY_CONTINUE", {
        routeName,
        recordingStartTime: startedAt,
      });
    }

    const lastPoint = Array.isArray(points) && points.length ? points[points.length - 1] : null;
    if (lastPoint && userPosition?.lat && userPosition?.lon) {
      const distM = haversine(
        { lat: Number(lastPoint.lat), lon: Number(lastPoint.lon) },
        { lat: Number(userPosition.lat), lon: Number(userPosition.lon) }
      );
      if (Number.isFinite(distM) && distM >= 250) {
        const keep = window.confirm(
          translate("nav.resume.farPrompt", { distanceM: Math.round(distM) })
        );
        if (!keep) {
          await usePanelsStore.getState().stopAndFinalizeTrack();
          await handleStartRecording();
          showToast(translate("nav.toast.startedNewRecording"), "success");
          return;
        }
      }
    }

    await handleResumeRecording();
  };

  const stopAndSaveRecording = async () => {
    try {
      let allPts = usePanelsStore.getState().points;

      if (Capacitor.isNativePlatform() && Capacitor.getPlatform?.() === "android") {
        const plugin =
          window?.Capacitor?.Plugins?.GPSNativePlugin ||
          window?.Capacitor?.Plugins?.GPSNative;

        if (plugin?.getRecordingSnapshot) {
          try {
            const res = await plugin.getRecordingSnapshot();
            const rawPoints = Array.isArray(res?.points) ? res.points : [];
            const normalized = rawPoints
              .map((p) => ({
                lat: p.lat,
                lon: p.lon,
                ele: Number.isFinite(p.ele ?? p.alt) ? (p.ele ?? p.alt) : null,
                acc: Number.isFinite(p.acc) ? p.acc : null,
                spd: Number.isFinite(p.spd) ? p.spd : null,
                ts: p.ts || p.time || Date.now(),
              }))
              .filter((p) => typeof p.lat === "number" && typeof p.lon === "number")
              .sort((a, b) => (a.ts || 0) - (b.ts || 0));

            if (normalized.length > allPts.length) {
              allPts = normalized;
              usePanelsStore.getState().setPoints(normalized);
            }
          } catch {}
        }
      }

      if (Array.isArray(allPts) && allPts.length > 1 && recordingStartTime) {
        const recordingLabel = hasManualTrailBase
          ? (activeTrail?.nome || trailData?.nome || trailData?.name || null)
          : null;
        const entry = {
          id: `rec-${Date.now()}`,
          name: recordingLabel || formatHistoryDefaultName(),
          date: new Date().toLocaleString(),
          activityMode,
          points: allPts,
          stats: {
            distKm: Number(distKm.toFixed(2)),
            totalMin: Number((totalSec / 60).toFixed(1)),
            movingMin: Number((movingSec / 60).toFixed(1)),
            stoppedMin: Number((stoppedSec / 60).toFixed(1)),
            speed: Number(avgMovingKmh.toFixed(1)),
            dPlus: Math.round(dPlus),
            points: allPts.length,
          },
          gpx: generateGPX(allPts),
        };

        const saved = JSON.parse(localStorage.getItem("historygps") || "[]");
        localStorage.setItem("historygps", JSON.stringify([entry, ...saved]));
        window.dispatchEvent(new CustomEvent("trekkin:history-updated"));
      }
    } catch {}

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform?.() === "android") {
      const plugin =
        window?.Capacitor?.Plugins?.GPSNativePlugin ||
        window?.Capacitor?.Plugins?.GPSNative;

      plugin?.setRecording?.({ recording: false });
      plugin?.clearRecordingSnapshot?.();
    }

    stopRecording();
    setPoints([]);
    setTrailData(null);
    setTrailBase([]);
    setActiveTrail(null);
  };

  const confirmAndStopRecording = async () => {
    if (!can("PanelNav", "stop_navigation")) {
      showToast(translate("nav.toast.stopNavDenied"));
      return;
    }
    if (stopInFlightRef.current) return;
    stopInFlightRef.current = true;
    heartRateRecoveryBlockedRef.current = true;
    setIsStopping(true);
    logEvent("info", "HEART_RATE_AUTO_RECOVERY_BLOCKED", {
      reason: "recording_stop_requested",
      status: useHeartRateStore.getState().status,
      sampleCount: useHeartRateStore.getState().samples?.length || 0,
    });

    let stopSucceeded = false;
    try {
      const recordingState = usePanelsStore.getState();
      const profile = useAuthStore.getState().user;
      const rawPoints = Array.isArray(recordingState.points) ? recordingState.points : [];
      const stoppedAt = Date.now();
      const healthMetrics = calculateRecordingMetrics(rawPoints, {
        startedAt: recordingState.recordingStartTime,
        stoppedAt,
        pausedMilliseconds: recordingState.recordingPausedDurationMs,
      });
      const healthHeartRate = useHeartRateStore.getState();
      const healthCalories = estimateActiveCalories({
        weightKg: profile?.weightKg,
        dateOfBirth: profile?.dob,
        sex: profile?.sex || profile?.gender,
        activityMode,
        movingMinutes: healthMetrics.movingSeconds / 60,
        totalMinutes: (healthMetrics.movingSeconds + healthMetrics.stoppedSeconds) / 60,
        speedKmh: healthMetrics.distanceKm > 0 && healthMetrics.movingSeconds > 0
          ? healthMetrics.distanceKm / (healthMetrics.movingSeconds / 3600)
          : 0,
        elevationGainM: healthMetrics.elevationGain,
        distanceKm: healthMetrics.distanceKm,
        heartRateSamples: healthHeartRate.samples,
        recordingStartedAt: recordingState.recordingStartTime,
        recordingStoppedAt: stoppedAt,
      });
      const heartRateSummary = await stopNativeHeartRate({
        workout: {
          activity: activityMode === ACTIVITY_MODE_BIKE ? "cycling" : "walking",
          startedAt: Number(recordingState.recordingStartTime) || null,
          stoppedAt,
          distanceMeters: healthMetrics.distanceKm * 1000,
          activeCalories: healthCalories?.activeKcal ?? null,
          points: rawPoints.map((point) => ({
            lat: Number(point?.lat),
            lon: Number(point?.lon ?? point?.lng),
            altitude: Number(point?.ele ?? point?.altitude ?? 0),
            ts: Number(point?.ts ?? point?.time),
          })).filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon)),
          heartRateSamples: (healthHeartRate.samples || []).map((sample) => ({
            bpm: Number(sample?.bpm),
            ts: Number(sample?.ts ?? sample?.time),
          })).filter((sample) => Number.isFinite(sample.bpm) && Number.isFinite(sample.ts)),
        },
      }).catch((error) => {
        logEvent("warn", "HEART_RATE_STOP_FAILED", {
          error: error?.message || String(error),
        });
        return null;
      });
      logEvent(heartRateSummary ? "info" : "warn", "HEART_RATE_BEFORE_HISTORY_SAVE", {
        sampleCount: heartRateSummary?.sampleCount || 0,
        averageBpm: heartRateSummary?.averageBpm || null,
        source: heartRateSummary?.source || null,
      });
      const finalizeResult = await withUiTimeout(
        usePanelsStore.getState().stopAndFinalizeTrack(),
        15000,
        "panel_nav_stop_timeout"
      );
      if (finalizeResult?.ok && finalizeResult?.entryId) {
        let summaryEntry = null;
        try {
          const history = JSON.parse(localStorage.getItem("historygps") || "[]");
          summaryEntry = Array.isArray(history)
            ? history.find((item) => item?.id === finalizeResult.entryId) || null
            : null;
        } catch {}
        setActivePanel("activitySummary", {
          entryId: finalizeResult.entryId,
          entrySnapshot: summaryEntry,
        });
      }
      stopSucceeded = true;
    } catch (error) {
      logEvent("error", "PANEL_NAV_STOP_FAILED", {
        error: error?.message || String(error),
        points: Array.isArray(usePanelsStore.getState().points)
          ? usePanelsStore.getState().points.length
          : 0,
      });
      showToast("Não foi possível terminar agora. O registo ficou no dispositivo.", "error");
    } finally {
      const recordingStillActive = !!usePanelsStore.getState().isRecording;
      if (!stopSucceeded && recordingStillActive) {
        heartRateRecoveryBlockedRef.current = false;
        logEvent("warn", "HEART_RATE_AUTO_RECOVERY_UNBLOCKED", {
          reason: "recording_stop_failed",
        });
        if (canUseHeartRate) {
          resumeNativeHeartRate({
            activity: activityMode === ACTIVITY_MODE_BIKE ? "cycling" : "walking",
          }).catch(() => {});
        }
      }
      stopInFlightRef.current = false;
      setIsStopping(false);
      setShowStopConfirm(false);
    }
  };
  
  // -------------------------------------------------------------
  // START/STOP permissões (FIX LOOP ANDROID)
  // -------------------------------------------------------------
  const handleRecord = async () => {
    if (!isRecording) {
      if (!can("PanelNav", "start_navigation")) {
        showToast(translate("nav.toast.startNavDenied"));
        return;
      }

      // 👉 ANDROID: disclosure antes de pedir background
      if (window?.Capacitor?.isNativePlatform?.() && Capacitor.getPlatform?.() === "android") {
        const plugin =
          window?.Capacitor?.Plugins?.GPSNativePlugin ||
          window?.Capacitor?.Plugins?.GPSNative;

        if (plugin?.checkPermissions) {
          try {
            let perm = await plugin.checkPermissions();
            if (perm?.location !== "granted") {
              await plugin.requestPermissions?.();
              perm = await plugin.checkPermissions();
            }

            if (perm?.background !== "granted") {
              if (!bgPermissionPromptedRef.current) {
                bgPermissionPromptedRef.current = true;
                pendingStartAfterBgRef.current = true;
                setShowBgPermission(true);
              }
              return;
            }

            try {
              localStorage.setItem("trekkin_bg_disclosure_accepted", "1");
            } catch {}
          } catch {
            // ignora totalmente
          }
        }
      }

      // ✅ Se chegou aqui, pode gravar
      if (isAndroidNative) {
        const plugin =
          window?.Capacitor?.Plugins?.GPSNativePlugin ||
          window?.Capacitor?.Plugins?.GPSNative;
        const batteryInfo = await refreshAndroidBatteryInfo();
        if (batteryInfo && !batteryInfo.ignoring) {
          const oem = [batteryInfo.manufacturer, batteryInfo.model]
            .filter(Boolean)
            .join(" ")
            .trim();
          const ask = window.confirm(
            `Para gravação estável em background${oem ? ` (${oem})` : ""}, ativa "Sem restrições" da bateria para a app. Queres abrir agora essa definição?`
          );
          if (ask) {
            try {
              await plugin?.requestIgnoreBatteryOptimizations?.();
            } catch {}
          }
        }
      }
      await handleStartRecording();
      return;
    }

    // -------------------------------------------------
    // STOP
    // -------------------------------------------------
    if (isRecording) {
      if (isRecordingPaused) {
        await handleResumeWithGuards();
        return;
      }
      if (!can("PanelNav", "stop_navigation")) {
        showToast(translate("nav.toast.stopNavDenied"));
        return;
      }
      handlePauseRecording("manual");
      return;
    }
  };

  useEffect(() => {
    if (!isRecording || isRecordingPaused) {
      autoPauseTriggeredRef.current = false;
      return;
    }
    const id = window.setInterval(() => {
      const pts = Array.isArray(usePanelsStore.getState().points)
        ? usePanelsStore.getState().points
        : [];
      if (pts.length < 2) return;
      const nowTs = Date.now();
      const recent = pts.filter((p) => nowTs - Number(p?.ts || 0) <= 5 * 60 * 1000);
      if (recent.length < 2) return;
      let movedM = 0;
      for (let i = 1; i < recent.length; i += 1) {
        movedM += haversine(recent[i - 1], recent[i]);
      }
      // Auto-pause por inatividade: manter dentro de ~50m na janela de 5 min.
      if (movedM <= 50 && !autoPauseTriggeredRef.current) {
        autoPauseTriggeredRef.current = true;
        handlePauseRecording("auto_inactive");
        showToast(translate("nav.toast.autoPausedInactive"), "warning");
      }
      // Rearme com histerese curta para evitar toggles.
      if (movedM > 65) autoPauseTriggeredRef.current = false;
    }, 30000);
    return () => window.clearInterval(id);
  }, [isRecording, isRecordingPaused, translate]);

  // -------------------------------------------------------------
  // Upload GPX permissões
  // -------------------------------------------------------------
  const importTrailFile = async (file) => {
    if (!can("PanelNav", "upload_gpx")) {
      showToast(translate("nav.toast.uploadGpxDenied"));
      return;
    }
    if (!file) return;

    try {
      const text = await file.text();
      if (!String(file.name || "").toLowerCase().endsWith(".gpx") || !/<gpx(?:\s|>)/i.test(text)) {
        showToast(translate("nav.toast.trailUploadError"), "error");
        return;
      }
      const id = `trail-${Date.now()}`;
      const baseName = prettifyTrailName(file.name);

      let parsed = null;
      let distKmVal = 0;

      try {
        parsed = await parseGPX(text);
        if (parsed?.points?.length > 1) {
          distKmVal = calcTotalDistance(parsed.points);
        }
      } catch {}

      const fullPoints = Array.isArray(parsed?.points) ? parsed.points : [];
      if (fullPoints.length < 2) {
        showToast(translate("nav.toast.trailUploadError"), "error");
        return;
      }
      const isNative = isNativeStorage();
      let nativeRef = null;
      if (isNative) {
        try {
          nativeRef = await writeTrailGpxNative(text, baseName);
        } catch {}
      }
      const newTrailBase = {
        id,
        nome: baseName,
        activityMode,
        dist: distKmVal ? `${distKmVal.toFixed(1)} km` : "—",
        elev: parsed?.totalAscent
          ? `${Math.round(parsed.totalAscent)} m D+`
          : "—",
        pointsTotal: fullPoints.length,
        native: nativeRef ? { trail: nativeRef } : undefined,
        date: new Date().toLocaleString(),
      };

      const trySaveTrail = (trailPoints) => {
        const trailEntry = {
          ...newTrailBase,
          points: trailPoints,
        };
        try {
          const currentTrails = JSON.parse(localStorage.getItem("trilhos") || "[]");
          const updated = [trailEntry, ...(Array.isArray(currentTrails) ? currentTrails : [])];
          localStorage.setItem("trilhos", JSON.stringify(updated));
          setTrilhos(updated);
          return true;
        } catch {
          return false;
        }
      };

      let saved = trySaveTrail(fullPoints);
      let reducedNotice = false;
      if (!saved) {
        saved = trySaveTrail(downsamplePoints(fullPoints, 20000));
        reducedNotice = saved;
      }
      if (!saved) {
        saved = trySaveTrail(downsamplePoints(fullPoints, 12000));
        reducedNotice = saved;
      }
      if (!saved) {
        saved = trySaveTrail(downsamplePoints(fullPoints, 6000));
        reducedNotice = saved;
      }
      if (!saved) {
        saved = trySaveTrail(downsamplePoints(fullPoints, 2000));
        reducedNotice = saved;
      }
      if (!saved) {
        showToast(translate("nav.toast.trailUploadError"), "error");
        return;
      }
      showToast(
        translate("nav.toast.trailUploaded", { name: baseName }),
        "success"
      );
      if (reducedNotice) {
        showToast("Trilho guardado com redução de pontos por limite de armazenamento.", "info");
      }
    } catch {
      showToast(translate("nav.toast.trailUploadError"), "error");
    }
  };

  const handleUploadTrail = async (e) => {
    const file = e.target.files?.[0];
    await importTrailFile(file);
    e.target.value = "";
  };

  useEffect(() => {
    const receive = () => {
      const payload = window.__trekkinSharedGpx;
      if (!payload?.text || !payload?.name) return;
      window.__trekkinSharedGpx = null;
      void importTrailFile({ name: payload.name, text: async () => payload.text });
    };
    window.addEventListener("trekkin:sharedGpxReady", receive);
    receive();
    return () => window.removeEventListener("trekkin:sharedGpxReady", receive);
  }, []);

  // -------------------------------------------------------------
  // Live Share permissões
  // -------------------------------------------------------------
  const handleToggleLiveShare = async () => {
    if (!can("PanelNav", "live_share")) {
      showToast(translate("nav.toast.liveShareDenied"));
      return;
    }

    const store = useShareStore.getState();
    if (store.isStartingShare) return;

    if (store.isSharing) {
      await store.stopShare();
      setLiveLink("");
      try { localStorage.removeItem("liveShareLink"); } catch {}
      showToast(translate("nav.toast.liveEnded"), "info");
      return;
    }

    if (!isRecording) {
      showToast("O Live Share só pode ser iniciado durante a gravação.", "warning");
      return;
    }
    if (isExpeditionMode) {
      showToast("Live Share indisponível no Modo Expedição.", "warning");
      return;
    }

    const link = await store.startShare();
    if (!link) return;

    setLiveLink(link);
    try { localStorage.setItem("liveShareLink", link); } catch {}
    showToast(translate("nav.toast.liveCreated"), "success");
  };

  // -------------------------------------------------------------
  // UI
  // -------------------------------------------------------------
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: "#333",
        position: "relative",
        paddingBottom: 20,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      {/* Fechar */}
      <div style={{ position: "absolute", top: 10, right: 10 }}>
        <CloseButton onClick={() => usePanelsStore.getState().resetToMap()} />
      </div>

      <h2
        style={{
          color: "#28a745",
          fontSize: "1.4rem",
          margin: "0 0 4px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <img src={`${base}icons/logo.png`} alt="" style={{ width: 28, height: 28 }} />
        <span>{translate("nav.title")}</span>
      </h2>

      <p style={{ marginTop: 0, marginBottom: 14, color: "#444", fontSize: 14 }}>
        {translate("nav.description")}
      </p>

      {isAndroidNative && isRecording && androidBatteryInfo && !androidBatteryInfo.ignoring && (
        <div
          style={{
            marginTop: -6,
            marginBottom: 10,
            background: "rgba(255, 193, 7, 0.14)",
            border: "1px solid rgba(255, 193, 7, 0.45)",
            borderRadius: 12,
            padding: "10px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12, color: "#7a5600", fontWeight: 700 }}>
            Android: otimização de bateria ativa. Isto pode parar a gravação em background.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <TrekkinButton
              onClick={async () => {
                const plugin =
                  window?.Capacitor?.Plugins?.GPSNativePlugin ||
                  window?.Capacitor?.Plugins?.GPSNative;
                try {
                  await plugin?.requestIgnoreBatteryOptimizations?.();
                } catch {}
              }}
              style={{ fontSize: 12, padding: "6px 10px" }}
            >
              Definir sem restrições
            </TrekkinButton>
            <TrekkinButton
              variant="ghost"
              onClick={refreshAndroidBatteryInfo}
              style={{ fontSize: 12, padding: "6px 10px" }}
            >
              Já ativei, verificar
            </TrekkinButton>
          </div>
        </div>
      )}

      {!isRecording && <div
        className="tk-theme-card tk-recording-mode-card"
        style={{
          marginTop: -4,
          marginBottom: 4,
          border: "1px solid rgba(0,0,0,0.12)",
          borderRadius: 12,
          background: isExpeditionMode ? "rgba(15, 81, 50, 0.08)" : "#fff",
          padding: 8,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 6,
          }}
        >
          {[
            {
              label: translate("nav.recording.activity"),
              icon: activityMode === ACTIVITY_MODE_BIKE ? "bicycle.svg" : "boot.svg",
              value: activityMode,
              onChange: (value) => setActivityMode?.(value),
              options: [
                { value: ACTIVITY_MODE_WALK, label: translate("nav.recording.walk") },
                { value: ACTIVITY_MODE_BIKE, label: translate("nav.recording.bike") },
              ],
            },
            {
              label: translate("nav.recording.mode"),
              icon: "recording-mode.svg",
              value: recordingMode,
              onChange: (value) => setRecordingMode?.(value),
              options: [
                { value: "normal", label: translate("nav.recording.normal") },
                { value: "expedition", label: translate("nav.recording.expedition") },
              ],
            },
            {
              label: "GPS",
              icon: "gps-profile.svg",
              value: recordingGpsProfile,
              onChange: handleRecordingGpsProfileChange,
              options: [
                { value: "precision", label: translate("nav.recording.precision") },
                { value: "balanced", label: translate("nav.recording.balanced") },
                { value: "battery", label: translate("nav.recording.battery") },
              ],
            },
          ].map((control) => (
            <label
              key={control.label}
              className="tk-recording-mode-control"
            >
              <img src={`./icons/${control.icon}`} alt="" aria-hidden="true" />
              <span>
                <b>{control.label}</b>
                <select
                  aria-label={control.label}
                  title={control.label}
                  value={control.value}
                  onChange={(event) => control.onChange(event.target.value)}
                  disabled={isRecording}
                >
                  {control.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </span>
            </label>
          ))}
        </div>
      </div>}

      {/* Métricas principais (apenas com gravação ativa) */}
      {isRecording && (
        <>
        <StatsPanelIcons
          base={base}
          variant="navRecording"
          forceVisible={isRecording}
          totalTime={formatHMS(totalSec)}
          stoppedTime={formatHMS(stoppedSec)}
          movingTime={formatHMS(movingSec)}
          speed={`${avgMovingKmh.toFixed(1)} km/h`}
          dPlus={`${Math.round(dPlus)} m`}
          distance={`${distKm.toFixed(2)} km`}
          altitude={`${Math.round(userPosition?.ele ?? 0)} m`}
          coords={
            lat && lon
              ? { lat: Number(lat.toFixed(5)), lon: Number(lon.toFixed(5)) }
              : null
          }
          points={`${points.length} pts`}
        />
        </>
      )}

      {/* Gráficos: seguimento + gravação */}
      {(showFollowChart || showRecordingChart) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {showFollowChart && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="tk-nav-chart-label" style={{ fontSize: 11, color: "#4b5563", fontWeight: 700 }}>
                Seguimento do trilho
              </span>
              <TrekkinChart
                height={followChartHeight}
                points={followChartPoints}
                useElevationMap
                showProgressOverlay={showFollowProgressMarker}
                progressPoint={followPosition}
                progressColor="#1f78ff"
                showElevationLine={showNavAltimetry}
                showElevationGuides
                padding={10}
                borderRadius={12}
                fillColor="rgba(40, 167, 69, 0.16)"
                showGrid
                showDistanceAxis
              />
              <div style={chartSeriesControlsStyle}>
                <label style={chartSeriesToggleStyle}>
                  <input
                    type="checkbox"
                    checked={showNavAltimetry}
                    onChange={(event) => setShowNavAltimetry(event.target.checked)}
                    style={{ accentColor: "#28a745" }}
                  />
                  <span style={{ ...chartSeriesSwatchStyle, background: "#28a745" }} />
                  Altimetria
                </label>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 11,
                  color: "#4b5563",
                }}
              >
                <span>
                  Distância restante:{" "}
                  <strong>{formatRemainingDistance(followEstimate?.remainingMeters)}</strong>
                </span>
                <span>
                  Tempo restante:{" "}
                  <strong>{formatRemainingTime(followEstimate?.remainingSec)}</strong>
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 11, color: "#6b7280" }}>
                  Sentido: <strong>{followDirectionReversed ? "Invertido" : "Original"}</strong>
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => setFollowDirectionReversed((prev) => !prev)}
                    style={{
                      border: "1px solid rgba(0,0,0,0.18)",
                      background: "white",
                      borderRadius: 8,
                      padding: "4px 8px",
                      fontSize: 11,
                      cursor: "pointer",
                    }}
                  >
                    {followDirectionReversed ? "Usar sentido original" : "Inverter sentido"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showRecordingChart && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="tk-nav-chart-label" style={{ fontSize: 11, color: "#4b5563", fontWeight: 700 }}>
                {translate("nav.chart.recording")}
              </span>
              <TrekkinChart
                height={recordingChartHeight}
                points={metricPoints}
                showElevationLine={showNavAltimetry}
                showElevationGuides
                padding={10}
                borderRadius={12}
                fillColor="rgba(40, 167, 69, 0.16)"
                showGrid
                showDistanceAxis
                heartRateSamples={canUseHeartRate ? heartRateSamples : null}
                showHeartRateLine={showNavHeartRate}
              />
              <div style={chartSeriesControlsStyle}>
                <label style={chartSeriesToggleStyle}>
                  <input
                    type="checkbox"
                    checked={showNavAltimetry}
                    onChange={(event) => setShowNavAltimetry(event.target.checked)}
                    style={{ accentColor: "#28a745" }}
                  />
                  <span style={{ ...chartSeriesSwatchStyle, background: "#28a745" }} />
                  {translate("nav.chart.altimetry")}
                </label>
                {canUseHeartRate && (
                  <label style={chartSeriesToggleStyle}>
                    <input
                      type="checkbox"
                      checked={showNavHeartRate}
                      onChange={(event) => setShowNavHeartRate(event.target.checked)}
                      style={{ accentColor: "#dc2626" }}
                    />
                    <span style={{ ...chartSeriesSwatchStyle, background: "#dc2626" }} />
                    {translate("nav.chart.heartRate")}
                  </label>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Botões principais */}
      <div
        className={`tk-nav-primary-actions${isRecording ? " is-recording" : ""}`}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: 10,
          alignItems: "center",
          width: "100%",
          minWidth: 0,
        }}
      >

        {/* UPLOAD GPX */}
        <TrekkinButton
          as="label"
          icon="nav-add.svg"
          variant="ghost"
          style={{
            width: "100%",
            flex: "none",
            minWidth: 0,
            minHeight: 0,
            boxSizing: "border-box",
            opacity: can("PanelNav","upload_gpx") ? 1 : 0.4,
            flexDirection: "column",
            gap: 4,
            padding: "8px 0",
            aspectRatio: "1 / 1",
          }}
        >
          <input
            type="file"
            accept=".gpx"
            onChange={handleUploadTrail}
            style={{ display: "none" }}
            disabled={!can("PanelNav","upload_gpx")}
          />
          <span style={{ fontSize: 10, lineHeight: 1.1, color: "#333", textAlign: "center" }}>
            <span style={{ whiteSpace: "pre-line" }}>{translate("nav.loadGpx")}</span>
          </span>
        </TrekkinButton>

        {/* START / PAUSE / RESUME */}
        <TrekkinButton
          icon={isRecording ? (isRecordingPaused ? "play.svg" : "stop.svg") : "start-record.svg"}
          variant={isRecording ? (isRecordingPaused ? "primary" : "warning") : "primary"}
          active={isRecording && !isRecordingPaused}
          onClick={handleRecord}
          disabled={
            (!isRecording && !can("PanelNav","start_navigation")) ||
            (isRecording && !can("PanelNav","stop_navigation"))
          }
          style={{
            width: "100%",
            flex: "none",
            minWidth: 0,
            minHeight: 0,
            boxSizing: "border-box",
            opacity:
              (!isRecording && !can("PanelNav","start_navigation")) ||
              (isRecording && !can("PanelNav","stop_navigation"))
                ? 0.4
                : 1,
            flexDirection: "column",
            gap: 4,
            padding: "8px 0",
            aspectRatio: "1 / 1",
            ...(isRecording
              ? isRecordingPaused
                ? {
                    background: "#16a34a",
                    borderColor: "#15803d",
                  }
                : {
                    background: "#f59e0b",
                    borderColor: "#d97706",
                  }
              : {}),
          }}
        >
          <span style={{ fontSize: 10, lineHeight: 1, color: "#333" }}>
            {isRecording
              ? (isRecordingPaused ? translate("nav.record.resume") : translate("nav.record.pause"))
              : translate("nav.record.start")}
          </span>
        </TrekkinButton>

        {/* STOP FINAL */}
        <TrekkinButton
          icon="stop-record.svg"
          variant="danger"
          onClick={() => {
            if (!isRecording) return;
            if (!can("PanelNav", "stop_navigation")) {
              showToast(translate("nav.toast.stopNavDenied"));
              return;
            }
            if (stopInFlightRef.current) return;
            setShowStopConfirm(true);
          }}
          disabled={!isRecording || !can("PanelNav","stop_navigation") || isStopping}
          style={{
            width: "100%",
            flex: "none",
            minWidth: 0,
            minHeight: 0,
            boxSizing: "border-box",
            opacity: isRecording && can("PanelNav","stop_navigation") ? 1 : 0.4,
            flexDirection: "column",
            gap: 4,
            padding: "8px 0",
            aspectRatio: "1 / 1",
            ...(isRecording
              ? {
                  background: "#dc2626",
                  borderColor: "#b91c1c",
                }
              : {}),
          }}
        >
          <span style={{ fontSize: 10, lineHeight: 1, color: "#333" }}>
            {translate("nav.record.finish")}
          </span>
        </TrekkinButton>

        {/* WAYPOINTS */}
        <TrekkinButton
          icon="waypoints.svg"
          variant="ghost"
          onClick={() => {
            if (!can("PanelNav", "waypoints")) {
              showToast(translate("nav.toast.waypointsDenied"));
              return;
            }
            setActivePanel("waypoints");
          }}
          disabled={!can("PanelNav","waypoints")}
          style={{
            width: "100%",
            flex: "none",
            minWidth: 0,
            minHeight: 0,
            boxSizing: "border-box",
            opacity: can("PanelNav","waypoints") ? 1 : 0.4,
            flexDirection: "column",
            gap: 4,
            padding: "8px 0",
            aspectRatio: "1 / 1",
          }}
        >
          <span style={{ fontSize: 10, lineHeight: 1, color: "#333" }}>
            {translate("nav.waypoints")}
          </span>
        </TrekkinButton>

        {/* LIVE SHARE */}
        <TrekkinButton
          icon="live.svg"
          variant={isSharing ? "danger" : "primary"}
          active={isSharing}
          onClick={() => {
            if (!can("PanelNav", "live_share")) {
              showToast(translate("nav.toast.liveShareDenied"));
              return;
            }
            if (!isSharing && !isRecording) {
              showToast("O Live Share só pode ser iniciado durante a gravação.", "warning");
              return;
            }
            if (!isSharing && isExpeditionMode) {
              showToast("Live Share indisponível no Modo Expedição.", "warning");
              return;
            }
            handleToggleLiveShare();
          }}
          disabled={
            !can("PanelNav","live_share") ||
            isStartingShare ||
            (!isSharing && (!isRecording || isExpeditionMode))
          }
          style={{
            width: "100%",
            flex: "none",
            minWidth: 0,
            minHeight: 0,
            boxSizing: "border-box",
            opacity:
              can("PanelNav","live_share") &&
              (isSharing || (isRecording && !isExpeditionMode))
                ? 1
                : 0.35,
            filter:
              can("PanelNav","live_share") &&
              (isSharing || (isRecording && !isExpeditionMode))
                ? "none"
                : "grayscale(1)",
            cursor:
              can("PanelNav","live_share") &&
              (isSharing || (isRecording && !isExpeditionMode))
                ? "pointer"
                : "not-allowed",
            flexDirection: "column",
            gap: 4,
            padding: "8px 0",
            aspectRatio: "1 / 1",
          }}
        >
          <span style={{ fontSize: 10, lineHeight: 1, color: "#333" }}>
            {isSharing
              ? translate("nav.liveShare.stop")
              : translate("nav.liveShare.start")}
          </span>
        </TrekkinButton>
      </div>

      {/* Navegação por waypoints */}
      <div
        className="tk-nav-waypoint-actions"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: 10,
          marginTop: 2,
          alignItems: "center",
          width: "100%",
          minWidth: 0,
        }}
      >
        <TrekkinButton
          icon="stop-nav-waypoint.svg"
          variant="ghost"
          active={hasAnyNavActive}
          onClick={() => {
            if (!can("PanelNav", "stop_nav_waypoint")) {
              showToast(translate("nav.toast.stopNavDenied"));
              return;
            }
            if (!hasAnyNavActive) return;
            clearNavWaypoint();
            setTrailBase([]);
            setTrailData(null);
            setActiveTrail(null);
            window.dispatchEvent(
              new CustomEvent("trekkin:navgrid-route", { detail: { path: [] } })
            );
            showToast(translate("nav.toast.navEnded"));
          }}
          disabled={!hasAnyNavActive || !can("PanelNav","stop_nav_waypoint")}
          style={{
            width: "100%",
            opacity: hasAnyNavActive && can("PanelNav","stop_nav_waypoint") ? 1 : 0.4,
            flexDirection: "column",
            gap: 4,
            padding: "8px 0",
            aspectRatio: "1 / 1",
          }}
        >
          <span style={{ fontSize: 10, lineHeight: 1, color: "#333" }}>
            {translate("nav.waypoint.stop")}
          </span>
        </TrekkinButton>
        <TrekkinButton
          icon="plus.svg"
          variant="ghost"
          onClick={() => {
            if (!can("PanelNav", "add_nav_waypoint")) {
              showToast(translate("nav.toast.waypointsDenied"));
              return;
            }
            setNavWaypointAddMode(true);
            showToast("Seleciona um waypoint no mapa para adicionar.");
            usePanelsStore.getState().resetToMap?.();
          }}
          disabled={!can("PanelNav","add_nav_waypoint")}
          style={{
            width: "100%",
            opacity: can("PanelNav","add_nav_waypoint") ? 1 : 0.4,
            flexDirection: "column",
            gap: 4,
            padding: "8px 0",
            aspectRatio: "1 / 1",
          }}
        >
          <span style={{ fontSize: 10, lineHeight: 1, color: "#333" }}>
            {translate("nav.waypoint.add")}
          </span>
        </TrekkinButton>
        <div
          className="tk-nav-waypoint-status"
          style={{
            gridColumn: "3 / 4",
            fontSize: 12,
            color: "#666",
            textAlign: "center",
            alignSelf: "center",
            minWidth: 0,
          }}
        >
          {navWaypointTarget
            ? translate("nav.waypoint.destination", {
                name: navWaypointTarget.name || translate("nav.fallback.waypoint"),
              })
            : navWaypointQueue.length
            ? translate("nav.waypoint.queue", { n: navWaypointQueue.length })
            : translate("nav.waypoint.none")}
        </div>
        <TrekkinButton
          icon="create-track.svg"
          variant="ghost"
          onClick={() => {
            if (!can("PanelNav", "create_track")) {
              showToast(translate("nav.toast.createTrailsDenied"));
              return;
            }
            window.dispatchEvent(
              new CustomEvent("trekkin:trail-draw-start", { detail: { allow: true } })
            );
            usePanelsStore.getState().resetToMap();
          }}
          disabled={!can("PanelNav","create_track")}
          style={{
            width: "100%",
            opacity: can("PanelNav","create_track") ? 1 : 0.4,
            flexDirection: "column",
            gap: 4,
            padding: "8px 0",
            aspectRatio: "1 / 1",
          }}
        >
          <span style={{ fontSize: 10, lineHeight: 1, color: "#333" }}>
            {translate("nav.createTrail")}
          </span>
        </TrekkinButton>
        <TrekkinButton
          icon="create-trail.svg"
          variant="ghost"
          onClick={() => {
            if (!can("PanelNav", "plan_route")) {
              showToast(translate("nav.toast.planRoutesDenied"));
              return;
            }
            if (!planActive) startPlanRoute?.();
            setActivePanel("waypoints", { mode: "planRoute" });
          }}
          disabled={!can("PanelNav","plan_route")}
          style={{
            width: "100%",
            opacity: can("PanelNav","plan_route") ? 1 : 0.4,
            flexDirection: "column",
            gap: 4,
            padding: "8px 0",
            aspectRatio: "1 / 1",
          }}
        >
          <span style={{ fontSize: 10, lineHeight: 1, color: "#333" }}>
            {translate("nav.createRoute")}
          </span>
        </TrekkinButton>
      </div>

      {isRecording && canUseHeartRate && <div
        className="tk-theme-card"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "10px 12px",
          borderRadius: 12,
          border: "1px solid rgba(220, 38, 38, 0.22)",
          background: "rgba(254, 242, 242, 0.9)",
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: "#7f1d1d", fontWeight: 800 }}>
            {translate("nav.heartRate.title")}
          </div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            {androidHeartRateReconnecting
              ? translate("nav.heartRate.searching")
              : heartRateDeviceName ||
              (heartRateStatus === "scanning" || heartRateStatus === "connecting"
                ? translate("nav.heartRate.searching")
                : heartRateStatus === "permissionDenied"
                  ? translate("nav.heartRate.permissionDenied")
                  : translate("nav.heartRate.noSensor"))}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <strong style={{ fontSize: 26, color: heartRateBpm && !androidHeartRateReconnecting ? "#dc2626" : "#9ca3af" }}>
            {androidHeartRateReconnecting ? "--" : heartRateBpm || "--"}
          </strong>
          <span style={{ marginLeft: 4, fontSize: 11, color: "#6b7280" }}>BPM</span>
          {heartRateSource && (
            <div style={{ fontSize: 9, color: "#9ca3af" }}>{heartRateSource}</div>
          )}
        </div>
      </div>}

      {(isRecording || hasAnyNavActive) && (
        <ActiveWeatherForecast position={userPosition || lastPoint} />
      )}

      {drawTrailPoints.length > 1 && !isTrailCreateMode && (
        <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <TrekkinButton
            onClick={() => setDrawTrailPreviewOpen(true)}
            style={{ fontSize: 12, padding: "6px 10px" }}
          >
            Ver percurso
          </TrekkinButton>
        </div>
      )}

      {isTrailCreateMode && (
        <div
          style={{
            marginTop: 12,
            borderRadius: 12,
            border: "1px solid rgba(0,0,0,0.12)",
            background: "#fff",
            padding: 10,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1b4332" }}>
            Criação de trilho
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <TrekkinButton
              onClick={saveCurrentDrawTrail}
              disabled={effectiveDrawTrailPoints.length < 2}
              style={{ fontSize: 12, padding: "6px 10px", opacity: effectiveDrawTrailPoints.length > 1 ? 1 : 0.45 }}
            >
              Gravar
            </TrekkinButton>
            <TrekkinButton
              variant="ghost"
              onClick={() => usePanelsStore.getState().resetToMap?.()}
              style={{ fontSize: 12, padding: "6px 10px" }}
            >
              Voltar ao Mapa
            </TrekkinButton>
          </div>
          {effectiveDrawTrailPoints.length > 1 && (
            <>
              <MiniMapRealTimeStatic
                points={effectiveDrawTrailPoints}
                height={140}
                color="#0f5132"
              />
              <TrekkinChart
                points={effectiveDrawTrailPoints}
                height={120}
                useElevationMap
                showElevationGuides
                padding={10}
                borderRadius={12}
                fillColor="rgba(40, 167, 69, 0.16)"
                showGrid
                showDistanceAxis
              />
            </>
          )}
        </div>
      )}

      {drawTrailPreviewOpen && !isTrailCreateMode && drawTrailPreviewPoints.length > 1 && (
        <div
          style={{
            marginTop: 12,
            borderRadius: 12,
            border: "1px solid rgba(0,0,0,0.12)",
            background: "#fff",
            padding: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <MiniMapRealTimeStatic
            points={drawTrailPreviewPoints}
            height={140}
            color="#0f5132"
          />
          <TrekkinChart
            points={drawTrailPreviewPoints}
            height={120}
            useElevationMap
            showElevationGuides
            padding={10}
            borderRadius={12}
            fillColor="rgba(40, 167, 69, 0.16)"
            showGrid
            showDistanceAxis
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <TrekkinButton
              variant="ghost"
              onClick={() => setDrawTrailPreviewOpen(false)}
              style={{ fontSize: 12, padding: "6px 10px" }}
            >
              Fechar
            </TrekkinButton>
            <TrekkinButton
              onClick={saveDrawnTrail}
              style={{ fontSize: 12, padding: "6px 10px" }}
            >
              Gravar
            </TrekkinButton>
          </div>
        </div>
      )}

      {/* LIVE Link */}
      {liveLink && (
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            marginTop: 12,
          }}
        >
          <div
            style={{
              flex: 1,
              padding: "10px 12px",
              fontSize: 14,
              color: "#222",
              borderRadius: 12,
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.15)",
            }}
          >
            {liveLink}
          </div>

          <TrekkinButton
            square
            icon="copy-link.svg"
            variant="primary"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(liveLink);
                showToast(translate("nav.toast.copyLinkSuccess"), "success");
              } catch {
                showToast(translate("nav.toast.copyLinkManual"), "error");
              }
            }}
          />
        </div>
      )}

      <div className="tk-nav-secondary-actions">
      <div>
        <TrekkinButton
          icon="history.svg"
          variant="ghost"
          className="tk-nav-link-button"
          onClick={() => {
            if (!can("PanelHistory","open")) {
              showToast(translate("nav.toast.historyDenied"));
              return;
            }
            setActivePanel("history");
          }}
          disabled={!can("PanelHistory","open")}
          style={{
            width: "100%",
            flex: "none",
            opacity: can("PanelHistory","open") ? 1 : 0.4,
          }}
        >
          <span>
            {translate("nav.history")}
          </span>
          <b aria-hidden="true">›</b>
        </TrekkinButton>
      </div>

      <div>
        <TrekkinButton
          icon="trail.svg"
          variant="ghost"
          className="tk-nav-link-button"
          onClick={() => setActivePanel("publicTrails")}
          style={{ width: "100%", flex: "none" }}
        >
          <span>
            {translate("nav.availableTrails")}
          </span>
          <b aria-hidden="true">›</b>
        </TrekkinButton>
      </div>

      <div>
        <TrekkinButton
          icon="cup-trophy.svg"
          variant="ghost"
          className="tk-nav-link-button"
          onClick={() => setActivePanel("gamification")}
          style={{ width: "100%", flex: "none" }}
        >
          <span>
            {translate("nav.missions")}
          </span>
          <b aria-hidden="true">›</b>
        </TrekkinButton>
      </div>

      <div>
        <TrekkinButton
          icon="location.svg"
          variant="ghost"
          className="tk-nav-link-button"
          onClick={() => {
            const next = !showTrailsOnMap;
            setShowTrailsOnMap(next);
            dispatchTrailsMapPayloads(next);
            if (next) setActivePanel("map");
          }}
          style={{ width: "100%", flex: "none" }}
        >
          <span>
            {showTrailsOnMap ? translate("nav.hideTrailsMap") : translate("nav.showTrailsMap")}
          </span>
          <b aria-hidden="true">›</b>
        </TrekkinButton>
      </div>
      </div>

      {/* Estado do GPS, imediatamente antes dos trilhos carregados. */}
      <HUDCard />

      {/* LISTA DE TRILHOS */}
      {visibleTrilhos.length === 0 ? (
        <p style={{ color: "#666", fontSize: 14, marginTop: 12 }}>
          {translate("nav.toast.noTrailsLoaded")}
        </p>
      ) : (
        visibleTrilhos.map((t) => (
          <TrailItem
            key={t.id}
            trail={t}
            base={base}
            setTrailData={setTrailData}
            setActivePanel={setActivePanel}
            saveTrilhos={saveTrilhos}
            trilhos={trilhos}
            isActive={
              hasManualTrailBase &&
              activeTrailIdForUi &&
              String(t?.id ?? "") === activeTrailIdForUi
            }
            handleStopTrail={() => {
              setTrailBase([]);
              setTrailData(null);
              setActiveTrail(null);
              showToast(translate("nav.toast.trailDisabled"), "info");
            }}
            handleStartTrail={async (trail) => {
              if (!can("PanelNav", "start_trail")) {
                showToast(translate("nav.toast.startLoadedTrailDenied"));
                return;
              }

              setTrailBase([]);

              try {
                const localPoints = Array.isArray(trail?.points) ? trail.points : [];
                let basePoints = localPoints.length > 1 ? localPoints : [];

                const tryParseGpxText = async (text) => {
                  const raw = String(text || "").trim();
                  if (!raw) return [];
                  const parsed = await parseGPX(raw);
                  return Array.isArray(parsed?.points) ? parsed.points : [];
                };

                const tryAdopt = (pts) => {
                  if (!Array.isArray(pts) || pts.length < 2) return;
                  if (!Array.isArray(basePoints) || pts.length > basePoints.length) {
                    basePoints = pts;
                  }
                };

                try {
                  const nativeGpx = await readTrailGpxNative(trail);
                  tryAdopt(await tryParseGpxText(nativeGpx));
                } catch {}

                tryAdopt(await tryParseGpxText(trail?.gpx));
                tryAdopt(await tryParseGpxText(trail?.file));

                try {
                  const rawUrl =
                    trail?.cloud?.url ||
                    trail?.cloud?.path ||
                    trail?.cloud?.file ||
                    trail?.url ||
                    null;
                  const fetchUrl = rawUrl
                    ? buildGpxFetchUrl(normalizeTrekkinUrl(String(rawUrl)))
                    : null;
                  if (fetchUrl) {
                    const res = await fetch(fetchUrl);
                    if (res.ok) {
                      const gpxText = await res.text();
                      tryAdopt(await tryParseGpxText(gpxText));
                    }
                  }
                } catch {}

                if (basePoints && basePoints.length > 0) {
                  setTrailBase(basePoints);
                  const enrichedTrail = { ...trail, points: basePoints };
                  setActiveTrail(enrichedTrail);
                  setTrailData(enrichedTrail);
                  if (!isRecording) {
                    try {
                      startRecording({ mode: isExpeditionMode ? "expedition" : "normal" });
                      showToast("Gravação iniciada automaticamente.", "success");
                    } catch {}
                  }
                  showToast(
                    translate("nav.toast.trailActive", { name: trail.nome }),
                    "info"
                  );
                  usePanelsStore.getState().resetToMap();
                } else {
                  setTrailBase([]);
                  setActiveTrail(null);
                  showToast(translate("nav.toast.trailNoValidPoints"), "error");
                }
              } catch {
                setTrailBase([]);
                setActiveTrail(null);
                showToast(translate("nav.toast.trailNoValidPoints"), "error");
              }
            }}
          />
        ))
      )}

      {showBgPermission && (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: 20,
            maxWidth: 320,
            textAlign: "center",
          }}
        >
          <h3 style={{ marginTop: 0 }}>📍 {translate("nav.bg.title")}</h3>

          <p style={{ fontSize: 14, color: "#444" }}>
            {translate("nav.bg.body")}
          </p>
          <ul style={{ textAlign: "left", fontSize: 14, color: "#444", paddingLeft: 18 }}>
            <li>{translate("nav.bg.bullet1")}</li>
            <li>{translate("nav.bg.bullet2")}</li>
            <li>{translate("nav.bg.bullet3")}</li>
          </ul>
          <p style={{ fontSize: 13, color: "#666" }}>
            {translate("nav.bg.note")}
          </p>

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <TrekkinButton
              variant="ghost"
              style={{ flex: 1 }}
              onClick={() => {
                pendingStartAfterBgRef.current = false;
                bgPermissionPromptedRef.current = false;
                setShowBgPermission(false);
                handleStartRecording();
              }}
            >
              {translate("nav.bg.skip")}
            </TrekkinButton>

            <TrekkinButton
              variant="primary"
              style={{ flex: 1 }}
              onClick={() => {
                bgPermissionPromptedRef.current = false;
                setShowBgPermission(false);

                try {
                  localStorage.setItem("trekkin_bg_disclosure_accepted", "1");
                } catch {}

                const plugin =
                  window?.Capacitor?.Plugins?.GPSNativePlugin ||
                  window?.Capacitor?.Plugins?.GPSNative;

                plugin?.requestBackgroundLocation?.();

                if (pendingStartAfterBgRef.current) {
                  pendingStartAfterBgRef.current = false;
                  handleStartRecording();
                }
              }}
            >
              {translate("nav.bg.continue")}
            </TrekkinButton>
          </div>
        </div>
      </div>
    )}

      <TrekkinConfirm
        visible={showStopConfirm}
        variant="recording"
        title={translate("nav.stopConfirm.title")}
        text={translate("nav.stopConfirm.text")}
        cancelLabel={translate("common.cancel")}
        confirmLabel={translate("nav.stopConfirm.action")}
        onCancel={() => setShowStopConfirm(false)}
        onConfirm={() => {
          setShowStopConfirm(false);
          confirmAndStopRecording();
        }}
      />

    </div>
  );
}

const chartSeriesToggleStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 11,
  fontWeight: 700,
  color: "#4b5563",
  cursor: "pointer",
  userSelect: "none",
};

const chartSeriesControlsStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 14,
  flexWrap: "wrap",
  width: "100%",
  minHeight: 24,
  marginTop: 4,
  padding: "0 6px",
  boxSizing: "border-box",
};

const chartSeriesSwatchStyle = {
  width: 16,
  height: 3,
  borderRadius: 999,
  display: "inline-block",
};
